import React, {
  type ComponentProps,
  type ComponentType,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigateToExternalMapView } from '../utils/navigateToExternalMapView';
import { Flex, Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import DashboardTitle from '../../../layout/DashboardTitle';
import dayjs from 'dayjs';
import EmptyShipments from '../EmptyShipments';
import {
  useShipmentReport,
  useShipmentReportCount,
} from './hooks/useShipmentReport';
import ShipmentDrawer, {
  type DrawerState as ShipmentDrawerState,
} from '../ShipmentDrawer';
import {
  GetHasShipmentBookingTemplateSelection,
  GetShipmentSummary,
  GetShipmentsVersion,
} from '../../../apis/shipment';
import type {
  ErrorLike,
  SingleShipmentDataExtended,
} from '../../../apis/types';
import type { ShipmentHeader, ShipmentRow } from './types';

import { useToken } from '../../../common/helper';
import { hideNewShipmentButtonForCompany } from '../../../common/constants';
import SavedFilterSetModal from './components/TabStrip/SavedFilterSetModal';
import { useShipmentSavedFilterSetModal } from './hooks/useShipmentSavedFilterSetModal';
import BulkUploadModal from '../../Modals/BulkUploadModal.jsx';
import CommonDrawer from '../../../components/CommonDrawer';
import CreateShipmentDrawer from '../CreateShipmentDrawer';
import BookShipmentDrawer from '../BookShipmentDrawer';
import QuoteShipmentDrawer from '../QuoteShipmentDrawer';
import CreateShellShipmentDrawer from '../CreateShellShipmentDrawer';
import ShipmentToolbar from '../ShipmentToolbar/ShipmentToolbar.jsx';
import useUnauthorize from '../../../hooks/useUnauthorize';
import ShipmentsFilterPanel from './components/ShipmentFiltersPanel';

import type { CalendarApi } from '@fullcalendar/core';

import {
  Shipment,
  SummaryData,
  DrawerStateFromHome,
  DrawerState,
  ShipmentRequestBody,
  ShipmentsVersionApiResponse,
  ToggleDrawerFn,
  AppliedFilter,
  FilterArrayItem,
  SummaryApiResponse,
  ShipmentFiltersPayload,
  CalendarShipmentItem,
  ID,
  CalendarViewBy,
  ShipmentTab,
} from './types';

import { useShipmentsEffects } from './hooks/useShipmentEffects';
import { useShipmentActivityOptimisticUpdates } from './hooks/useShipmentActivityOptimisticUpdates';
import { useShipmentSelection } from './hooks/useShipmentSelection';
import { useShipmentPagination } from './hooks/useShipmentPagination';
import { useShipmentPermissions } from './hooks/useShipmentPermissions';
import { useManualShipmentCreationFlags } from '@/hooks/useManualShipmentCreationFlags';
import SummaryCards from './components/SummaryCards';
import { useShipmentsFiltersController } from './hooks/useShipmentFiltersController';
import { useLegScopedProgressFilter } from './hooks/useLegScopedProgressFilter';
import { CalendarView } from './components/calendar/CalendarView';
import SwitchButtons from './components/SwitchButtons';
import { useCalendarShipments } from './hooks/useCalendarShipments';
import CalendarViewMore from './components/calendar/CalendarViewMore';
import { useCalendarViewportCounts } from './hooks/useCalendarViewPortCounts';
import { useDispatch, useSelector } from 'react-redux';
import { clearShipmentNav } from '@/redux-toolkit/slice/shipmentNavigationSlice';
import {
  addTab,
  setActiveTabId,
  setCustomTabs,
  setDefaultTabs,
} from '@/redux-toolkit/slice/shipmentTabSlice';
import { RootState } from '@/redux-toolkit/store';
import { ZOOMLOGI_CLOSE_UI_OVERLAYS_EVENT } from '@/common/zoomlogiUiEvents';
import { CalendarIcon, ListIcon } from '@/assets/icons/icons';
import { buildShipmentFiltersPayload } from './utils/buildShipmentFiltersPayload';
import { buildMassActionFiltersPayload } from './utils/buildMassActionFiltersPayload';
import { getSelectionScopeCount } from './utils/getSelectionScopeCount';
import { getVisibleCalendarShipmentIds } from './utils/calendarSelection';
import TabStrip from './components/TabStrip';
import ShipmentColumnsDropdown from './components/ShipmentColumnsDropdown';
import useViewFiltersModal from './hooks/useViewFilterModal';
import ViewFiltersModal from './components/ViewFilterModal';
import useFetchCompanyCouriers from '../../../hooks/useFetchCompanyCouriers';
import { hasQuickStatCompanyCourier } from '../../../common/utils/quickStatCompanyCouriers';
import { isCryoportCompany } from '../../../common/constants/cryoport';
import { isWorldCourierCompany } from '../../../common/constants/worldCourier';
import useSpecialtyProviders from '../../../hooks/useSpecialtyProviders';
import { filtersConfig } from '@/common/static_data';
import ShipmentVirtualTable, {
  ShipmentTableActionKey,
} from './components/shipment/ShipmentVirtualTable';
import ShipmentMobileCards from './components/shipment/ShipmentMobileCards';
import { normalizeShipmentHeaders } from './components/shipment/shipmentTableUtils';
import { SKELETON_HEADERS } from './components/shipment/shipmentTableConstants';
import { filterItemsByLegType, getLegTypeFromSearch } from './legTypeUtils';
import { useShipmentSort } from './hooks/useShipmentSort';
import { useCompanionShipmentsHandoff } from './hooks/useCompanionShipmentsHandoff';
import ShipmentSort from './components/ShipmentSort';
import { getShipmentTabs } from '@/apis/shipmentTabs';
import { useMassAction } from '../ShipmentToolbar/useMassAction';
import PerShipmentNotificationModal from '../PerShipmentNotification/PerShipmentNotificationModal';
import type { NotificationData } from '../PerShipmentNotification/OneTimeNotificationModal';
import { CustomModalWithoutButton } from '@/components';
import ShareShipment from '@/pages/Modals/ShareShipment';
import { ArchiveShipmentModal } from '../components';
import MarkAsDeliveredModal from './MarkAsDeliveredModal';
import { CustomDeleteModal } from '@/pages/Modals';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import type { HeaderItem } from '@/pages/Data/types';
import useFilterLookups from '@/hooks/useFilterLookups';

type ToolbarOverrides = {
  excludedIds: string[];
  selectableItems: ShipmentRow[];
  filteredData: ShipmentRow[];
  selectionScopeCount: number | null | undefined;
};

// The JSX toolbar infers `never[]`/`null` from its default props; these are the real types
const TypedShipmentToolbar = ShipmentToolbar as ComponentType<
  Omit<ComponentProps<typeof ShipmentToolbar>, keyof ToolbarOverrides> &
    ToolbarOverrides
>;

const SHIPMENT_PAGE_SIZE = 100;
const LIST_POLL_INTERVAL_MS = 60000; // 1 minute
const LIST_FORCE_REFRESH_MS = 120000;
const SHIPMENT_LOADING_ROW_COUNT = 8;
const SHIPMENT_PLACEHOLDER_ROW_KEY = '__shipment_placeholder_key';
const NEW_SHIPMENT_PLACEHOLDER_ROW_KEY = 'new-shipment-first-row-skeleton';

const isProgressAppliedFilter = (filter: AppliedFilter) =>
  filter.filterBy === 'status' || filter.dateFilter === 'status';

const withoutProgressAppliedFilters = (filters: AppliedFilter[]) =>
  filters.filter((filter) => !isProgressAppliedFilter(filter));

const withoutProgressFilterArray = (filters: FilterArrayItem[]) =>
  filters.filter((filter) => filter.filterBy !== 'status');

const Shipments: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { xs, sm } = useBreakpoints();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { token: tokenOrNull, decodedUserToken, checkTokenExpiry } = useToken();
  const token = tokenOrNull ?? '';
  const decodedToken = decodedUserToken();
  const worldCourierEnabled = useMemo(
    () => isWorldCourierCompany(decodedToken?.enablecustomerleg),
    [decodedToken?.enablecustomerleg]
  );
  const cryoportEnabled = useMemo(
    () => isCryoportCompany(decodedToken?.company_id),
    [decodedToken?.company_id]
  );
  const hideNewShipmentButton = useMemo(
    () => hideNewShipmentButtonForCompany(decodedToken?.company_id),
    [decodedToken?.company_id]
  );
  const manualShipmentFlags = useManualShipmentCreationFlags();
  const { unauthorize } = useUnauthorize();
  const handleUnauthorized = useCallback(
    (error: unknown) => unauthorize(error as ErrorLike),
    [unauthorize]
  );
  const { t } = useTranslation('shipments');
  const {
    allPermissions,
    permissions,
    isSupportCompany,
    hasOperationalLegView,
  } = useShipmentPermissions();
  const { couriers } = useFetchCompanyCouriers();
  const hasQuickStat = useMemo(
    () => hasQuickStatCompanyCourier(couriers),
    [couriers]
  );
  const hasSpecialtyBooking = useSpecialtyProviders(token).length > 0;
  const directBookingEnabled =
    worldCourierEnabled || cryoportEnabled || hasSpecialtyBooking;

  /** At least one booking template row (template_selection) for QuickSTAT book menu. */
  const [bookingTemplatesLoading, setBookingTemplatesLoading] = useState(true);
  const [hasBookingTemplates, setHasBookingTemplates] = useState(false);
  useEffect(() => {
    if (!token) {
      setBookingTemplatesLoading(false);
      setHasBookingTemplates(false);
      return;
    }
    let cancelled = false;
    setBookingTemplatesLoading(true);
    GetHasShipmentBookingTemplateSelection(token)
      .then((res) => {
        const has = Boolean(res?.data?.data?.data?.has_template_selection);
        if (!cancelled) setHasBookingTemplates(has);
      })
      .catch(() => {
        if (!cancelled) setHasBookingTemplates(false);
      })
      .finally(() => {
        if (!cancelled) setBookingTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  /**
   * Book is greyed unless World Courier / Cryoport / a specialty API carrier
   * is allowed, or QuickStat is linked and (templates still loading, or at
   * least one template exists). Company Settings can hide Book entirely by
   * unchecking it.
   */

  const bookShipmentMenuDisabled = useMemo(() => {
    if (directBookingEnabled) return false;
    if (!hasQuickStat) return true;
    if (bookingTemplatesLoading) return false;
    return !hasBookingTemplates;
  }, [
    directBookingEnabled,
    hasQuickStat,
    bookingTemplatesLoading,
    hasBookingTemplates,
  ]);
  const filters = useShipmentsFiltersController();
  const filterLookups = useFilterLookups();
  const {
    setAppliedFilters,
    setFilterArray,
    setAppliedSearchQuery,
    setActiveViewType,
    setActiveLegType,
  } = filters;
  const dispatch = useDispatch();
  const shipmentNav = useSelector(
    (state: RootState) => state.shipmentNavigation
  );
  const {
    defaultTabs,
    customTabs,
    additionalFiltersPerTab,
    activeTabId,
    hasLoaded: shipmentTabsLoaded,
  } = useSelector((state: RootState) => state.shipmentTab);
  const fetchRef = useRef<
    (
      searchquery: string,
      check: boolean,
      pageArg?: number,
      pageSizeArg?: number,
      calledFrom?: string,
      useMissingIds?: boolean,
      missingIdsOverride?: string[] | null,
      payloadOverride?: ShipmentFiltersPayload | null,
      tabIdOverride?: string | null,
      sortKeyOverride?: string,
      sortOrderOverride?: 'ascending' | 'descending',
      legTypeOverride?: 'CL' | 'EL'
    ) => Promise<unknown> | void
  >(() => Promise.resolve());

  const [_selectedShipmentID, setSelectedShipmentID] = useState<string | null>(
    null
  );
  const calendarApiRef = useRef<CalendarApi | null>(null);
  const [summaryData, setSummaryData] = useState<SummaryData>({});
  const [selectAllAcrossFilters, setSelectAllAcrossFilters] = useState(false);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const [shipmentReportPayload, setShipmentReportPayload] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [reportHeaders, setReportHeaders] = useState<ShipmentHeader[]>([]);
  const [columnConfigHeaders, setColumnConfigHeaders] = useState<
    ShipmentHeader[]
  >([]);
  const [shipmentsData, setShipmentsData] = useState<ShipmentRow[]>([]);
  const [filteredData, setFilteredData] = useState<ShipmentRow[]>([]);
  const pendingColumnHeadersRef = useRef<ShipmentHeader[] | null>(null);
  const reportHeadersRef = useRef<ShipmentHeader[]>([]);

  const [recentlyCreatedShipmentId, setRecentlyCreatedShipmentId] = useState<
    string | null
  >(null);
  // dispaly create shipment api res
  const [recentlyCreatedShipmentData, setRecentlyCreatedShipmentData] =
    useState<ShipmentRow | null>(null);
  const [newShipmentSkeletonWindowActive, setNewShipmentSkeletonWindowActive] =
    useState(false);

  const [resolvedShipmentPayloadKey, setResolvedShipmentPayloadKey] = useState<
    string | null
  >(null);
  const [resolvedShipmentTotal, setResolvedShipmentTotal] = useState<
    number | null
  >(null);
  const [isSilentShipmentRefresh, setIsSilentShipmentRefresh] = useState(false);

  const [drawerStateFromHome, setDrawerStateFormHome] =
    useState<DrawerStateFromHome>({
      dState: location?.state?.drawerState ?? '',
      shipmentId: location?.state?.shipmentId ?? null,
      cocId: location?.state?.CoC_ID ?? null,
    });

  const [showBulkUploadModal, setShowBulkUploadModal] =
    useState<boolean>(false);
  const [startAlertsInterval] = useState<boolean>(true);

  const isSearchTriggered = useMemo(() => {
    const applied = (filters.appliedSearchQuery ?? '').trim();
    return applied.length > 0;
  }, [filters.appliedSearchQuery]);

  const [highlightedShipmentId, setHighlightedShipmentId] = useState<
    string | null
  >(null);

  const [drawerState, setDrawerState] = useState<DrawerState>({
    isOpen: false,
    name: '',
    shipmentId: null,
    alert: null,
  });
  const [drawerLegType, setDrawerLegType] = useState<'CL' | 'EL'>();
  const [overAllShipmentCount, setOverAllShipmentCount] = useState<number>(0);
  const [openCreateShipmentDrawer, setOpenCreateShipmentDrawer] =
    useState<boolean>(false);
  const [openBookShipmentDrawer, setOpenBookShipmentDrawer] =
    useState<boolean>(false);
  const [openQuoteShipmentDrawer, setOpenQuoteShipmentDrawer] =
    useState<boolean>(false);
  const [openCreateShellDrawer, setOpenCreateShellDrawer] =
    useState<boolean>(false);
  const [quotePrefillShipment, setQuotePrefillShipment] = useState<
    SingleShipmentDataExtended | undefined
  >(undefined);
  const [quoteDrawerMode, setQuoteDrawerMode] = useState<'quote' | 'return'>(
    'quote'
  );
  const [bookPrefillShipment, setBookPrefillShipment] = useState<
    SingleShipmentDataExtended | undefined
  >(undefined);
  // Bumped by QuoteShipmentDrawer after a successful promote so the
  // ShipmentDetail drawer refetches and the Quote/Book bars disappear.
  const [detailRefreshKey, setDetailRefreshKey] = useState<number>(0);
  const [moreDrawerEventDatetime, setMoreDrawerEventDatetime] =
    useState<string>('');
  const [moreDrawerViewBy, setMoreDrawerViewBy] =
    useState<CalendarViewBy>('arrival');

  const activeViewType = filters.activeViewType ?? 'list';
  const activeLegType = filters.activeLegType ?? 'CL';
  const shipmentStatusOptionsByLeg = useMemo(
    () => ({
      CL: filterLookups.shipmentStatusDDL,
      EL: filterLookups.shipmentStatusELDDL ?? [],
    }),
    [filterLookups.shipmentStatusDDL, filterLookups.shipmentStatusELDDL]
  );
  const {
    prepareProgressFiltersForLeg,
    sanitizeProgressFiltersForLeg,
    sanitizeProgressPayloadForLeg,
  } = useLegScopedProgressFilter({
    activeLegType,
    appliedFilters: filters.appliedFilters,
    filterArray: filters.filterArray,
    statusOptionsByLeg: shipmentStatusOptionsByLeg,
    progressFiltersByLeg: filters.progressFiltersByLeg,
    setAppliedFilters,
    setFilterArray,
    setProgressFiltersByLeg: filters.setProgressFiltersByLeg,
  });

  useEffect(() => {
    const sanitizedFilters = sanitizeProgressFiltersForLeg(activeLegType, {
      appliedFilters: filters.appliedFilters,
      filterArray: filters.filterArray,
    });
    const filtersChanged =
      JSON.stringify(sanitizedFilters.appliedFilters) !==
        JSON.stringify(filters.appliedFilters) ||
      JSON.stringify(sanitizedFilters.filterArray) !==
        JSON.stringify(filters.filterArray);

    if (!filtersChanged) return;

    setAppliedFilters(sanitizedFilters.appliedFilters);
    setFilterArray(sanitizedFilters.filterArray);
  }, [
    activeLegType,
    filters.appliedFilters,
    filters.filterArray,
    sanitizeProgressFiltersForLeg,
    setAppliedFilters,
    setFilterArray,
  ]);

  const isCalendarActive = activeViewType === 'calendar';
  const maxSelect = activeViewType === 'calendar' ? null : 50;

  const isCalendarActiveRef = useRef(isCalendarActive);
  useEffect(() => {
    isCalendarActiveRef.current = isCalendarActive;
  }, [isCalendarActive]);

  const didConsumeHomeCreateDrawerIntentRef = useRef(false);
  useEffect(() => {
    if (didConsumeHomeCreateDrawerIntentRef.current) return;

    const state = (location.state ?? {}) as Record<string, unknown>;
    const shouldOpen =
      state.__zl_open_create_shipment_drawer === true &&
      state.__zl_source === 'home_empty_shipments';

    if (!shouldOpen) return;
    if (!manualShipmentFlags.loaded) return;

    didConsumeHomeCreateDrawerIntentRef.current = true;
    if (manualShipmentFlags.track) {
      setOpenCreateShipmentDrawer(true);
    }

    const {
      __zl_open_create_shipment_drawer: _ignore1,
      __zl_source: _ignore2,
      ...rest
    } = state as Record<string, unknown>;

    const _ = navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: rest }
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    manualShipmentFlags.loaded,
    manualShipmentFlags.track,
    navigate,
  ]);
  const {
    data: shipmentReport,
    error,
    isFetching: isFetchingShipmentReport,
    isLoading: isLoadingShipmentReport,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useShipmentReport({
    token,
    payload: shipmentReportPayload,
  });

  const firstPageCountPending = Boolean(shipmentReport?.pages[0]?.countPending);
  const { data: shipmentCountData } = useShipmentReportCount({
    token,
    payload: shipmentReportPayload,
    enabled: firstPageCountPending,
  });
  const isShipmentReportRequestInFlightRef = useRef(false);

  useEffect(() => {
    isShipmentReportRequestInFlightRef.current =
      isLoadingShipmentReport || isFetchingShipmentReport;
  }, [isFetchingShipmentReport, isLoadingShipmentReport]);

  useEffect(() => {
    if (!error) return;
    unauthorize(error as ErrorLike);
  }, [error, unauthorize]);

  const {
    handleCocCommentCreated,
    handleShipmentNotificationActivityChange,
    markShipmentCommentReadMutation,
  } = useShipmentActivityOptimisticUpdates({
    token,
    shipmentsData,
    filteredData,
    setShipmentsData,
    setFilteredData,
  });
  const { mutate: markShipmentCommentsRead } = markShipmentCommentReadMutation;

  const handleShipmentCommentsRead = useCallback(
    (shipmentId: string) => {
      markShipmentCommentsRead({
        shipmentId,
      });
    },
    [markShipmentCommentsRead]
  );

  const {
    viewFiltersTab,
    viewFiltersModalOpen,
    handleOpenViewFiltersModal,
    handleCloseViewFiltersModal,
  } = useViewFiltersModal();

  const {
    selectedIds,
    setSelectedIds,
    selectedShipmentStatuses,
    setSelectedShipmentStatuses,
    checked,
    setChecked,
    handleSelect,
  } = useShipmentSelection(maxSelect);

  // Reset multi-select state and close multi-select drawers when switching leg types (CL <-> EL)
  useEffect(() => {
    setSelectedIds([]);
    setSelectedShipmentStatuses({});
    setChecked(false);
    setSelectAllAcrossFilters(false);
    setExcludedIds([]);
    setDrawerState((prev) => {
      if (prev.isOpen && prev.name === 'shipmentProductsList') {
        return { isOpen: false, name: '', shipmentId: null, alert: null };
      }
      return prev;
    });
  }, [
    activeLegType,
    setSelectedIds,
    setSelectedShipmentStatuses,
    setChecked,
    setSelectAllAcrossFilters,
    setExcludedIds,
  ]);

  const {
    page,
    setPage,
    pageSize,
    setTotalPages,
    totalShipments,
    setTotalShipments,
  } = useShipmentPagination(1, SHIPMENT_PAGE_SIZE);

  const { handleMassAction } = useMassAction();

  const [tableActionShipmentID, setTableActionShipmentID] = useState<
    string | null
  >(null); // to keep track of which shipment's ID
  const [openNotificationModal, setOpenNotificationModal] = useState(false); // setup notificaiton modal
  const [notificationType, setNotificationType] = useState<number>(1); // notificaiton type
  const [shareShipmentModalOpen, setShareShipmentModalOpen] = useState(false); // share shipment modal
  const [archiveShipmentModalOpen, setArchiveShipmentModalOpen] =
    useState(false); // archive shipment modal
  const [showDeliveredModal, setShowDeliveredModal] = useState(false); // mark as delivered modal
  const [selectedShipmentForDelivered, setSelectedShipmentForDelivered] =
    useState<ShipmentRow | null>(null); // to keep track of which shipment is selected for marking as delivered
  const [dismissAlertsModalVisible, setDismissAlertsModalVisible] =
    useState(false); // dismiss alerts confirmation modal
  const [shipmentIdToDismiss, setShipmentIdToDismiss] = useState<string | null>(
    null
  ); // to keep track of which shipment ID to dismiss alerts for

  const shipmentsVersionRef = useRef<number | null>(null);
  const lastSummaryPayloadRef = useRef<string | null>(null);
  const lastShipmentsFetchRef = useRef<number>(0);
  const lastSummaryFetchRef = useRef<number>(0);
  const pageRef = useRef<number>(page);
  const appendingPageRef = useRef(false);
  const moreDrawerPageRef = useRef<number>(1);
  const pageSizeRef = useRef<number>(pageSize);
  const isTabVisibleRef = useRef<boolean>(!document.hidden);
  const [drawerSource, setDrawerSource] = useState<'list' | 'calendar'>('list');
  const [moreDrawerSource, setMoreDrawerSource] = useState<
    'header' | 'slot' | 'month' | undefined
  >(undefined);

  const searchVisibleRef = useRef<boolean>(filters.isSearchVisible);
  const searchQueryRef = useRef<string>(filters.appliedSearchQuery ?? '');
  const [moreDrawerOpen, setMoreDrawerOpen] = useState(false);
  const moreDrawerSortRef = useRef<{
    key: string;
    order: 'asc' | 'desc';
  } | null>(null);
  const didRunInitialFetchRef = useRef(false);
  const didInitialQueryStringEffect = useRef(false);
  const [moreDrawerSlotStart, setMoreDrawerSlotStart] = useState<string | null>(
    null
  );
  const [moreDrawerSlotEnd, setMoreDrawerSlotEnd] = useState<string | null>(
    null
  );
  const returnToMoreDrawerRef = useRef(false);
  const pendingSortTabsRefreshRef = useRef(false);

  const refreshShipmentTabs = useCallback(async () => {
    if (!token) return;

    const data = await getShipmentTabs(token);
    dispatch(setDefaultTabs(data.defaultTabs));
    dispatch(setCustomTabs(data.customTabs));
  }, [dispatch, token]);

  const calendarTeamIds = useMemo(() => {
    const team = (filters.filterArray ?? []).find((f) => f.filterBy === 'team');
    return Array.isArray(team?.filterValue) ? team!.filterValue : [];
  }, [filters.filterArray]);

  const calendarCourierIds = useMemo(() => {
    const courier = (filters.filterArray ?? []).find(
      (f) => f.filterBy === 'courier'
    );
    return Array.isArray(courier?.filterValue) ? courier!.filterValue : [];
  }, [filters.filterArray]);

  const calendarItemToShipment = (item: CalendarShipmentItem): Shipment => {
    return {
      id: String(item.shipment_id),
      status: item.status as Shipment['status'],
    } as Shipment;
  };
  const dynamicDropdownHeight = `calc(100vh - 350px)`;

  const activeTabSort = useMemo(() => {
    const defaultTab = defaultTabs.find((tab) => tab.id === activeTabId);
    const customTab = customTabs.find((tab) => tab.id === activeTabId);
    const tab = defaultTab ?? customTab;

    return {
      sort_key: tab?.sort_key,
      sort_order: tab?.sort_order,
    };
  }, [activeTabId, customTabs, defaultTabs]);
  const activeCustomTab = useMemo(
    () => customTabs.find((tab) => tab.id === activeTabId),
    [activeTabId, customTabs]
  );
  const activeTabColumnsId = activeCustomTab?.savedFilterSet?.id || activeTabId;
  useEffect(() => {
    reportHeadersRef.current = reportHeaders;
  }, [reportHeaders]);

  useEffect(() => {
    pendingColumnHeadersRef.current = null;
    setColumnConfigHeaders([]);
  }, [activeTabColumnsId]);

  // Home companion panel handoff ("See all shipments"). Everything arrives
  // via location.state (no Redux) — this hook is fully self-contained; we
  // just apply its results through the tab state this page already has.
  const {
    isResolving: isResolvingCompanionHandoff,
    resolvedTabId: companionResolvedTabId,
    resolvedSortKey: companionSortKey,
    resolvedSortOrder: companionSortOrder,
    newlyPinnedTab: companionNewlyPinnedTab,
  } = useCompanionShipmentsHandoff({
    token,
    customTabs,
    tabsLoaded: shipmentTabsLoaded,
  });

  useEffect(() => {
    if (companionResolvedTabId)
      dispatch(setActiveTabId(companionResolvedTabId));
  }, [companionResolvedTabId, dispatch]);

  useEffect(() => {
    if (companionNewlyPinnedTab) dispatch(addTab(companionNewlyPinnedTab));
  }, [companionNewlyPinnedTab, dispatch]);

  // The companion sort only applies while we're still on the tab the
  // handoff landed on — captured once (on the first render where a
  // resolution exists) so a later manual tab switch isn't affected.
  const companionSortTargetTabIdRef = useRef<string | undefined>(undefined);
  if (
    companionSortKey &&
    companionSortTargetTabIdRef.current === undefined &&
    !isResolvingCompanionHandoff
  ) {
    companionSortTargetTabIdRef.current = companionResolvedTabId ?? activeTabId;
  }
  const isOnCompanionSortTargetTab =
    companionSortTargetTabIdRef.current !== undefined &&
    activeTabId === companionSortTargetTabIdRef.current;

  const resolvedActiveTabSortKey =
    (isOnCompanionSortTargetTab && companionSortKey) ||
    activeTabSort.sort_key ||
    'scheduled_delivery_date';
  const resolvedActiveTabSortOrder =
    (isOnCompanionSortTargetTab && companionSortOrder) ||
    activeTabSort.sort_order ||
    'ascending';

  const {
    sortKey,
    sortOrder,
    sortMenu,
    selectedSortLabel,
    handleSortOrderToggle,
  } = useShipmentSort({
    tabId: activeTabId,
    initialSortKey: resolvedActiveTabSortKey,
    initialSortOrder: resolvedActiveTabSortOrder,
    onUserSortChange: () => {
      pendingSortTabsRefreshRef.current = true;
    },
  });
  const shipmentTabsReadyForFetch =
    shipmentTabsLoaded &&
    !isResolvingCompanionHandoff &&
    sortKey === resolvedActiveTabSortKey &&
    sortOrder === resolvedActiveTabSortOrder;
  const buildShipmentRequestBody = useCallback(
    (
      searchquery: string,
      pageVal: number,
      pageSizeVal: number,
      payloadOverride?: ShipmentFiltersPayload | null,
      tabIdOverride?: string | null,
      sortKeyOverride?: string,
      sortOrderOverride?: 'ascending' | 'descending',
      legTypeOverride?: 'CL' | 'EL'
    ): ShipmentRequestBody => {
      const trimmedSearch = (searchquery || '').trim();
      const effectiveTabId = tabIdOverride ?? activeTabId;
      const effectiveLegType = legTypeOverride ?? activeLegType;
      const effectiveSortKey = sortKeyOverride ?? sortKey;
      const effectiveSortOrder = sortOrderOverride ?? sortOrder;
      const effectiveCustomTab = customTabs.find(
        (tab) => tab.id === effectiveTabId
      );
      const apiTabId = effectiveCustomTab?.savedFilterSet?.id ?? effectiveTabId;

      const rawPayload = (() => {
        if (payloadOverride === null) {
          return {};
        }

        if (payloadOverride && typeof payloadOverride === 'object') {
          return payloadOverride;
        }

        if (effectiveCustomTab) {
          const baseAppliedFilters =
            effectiveCustomTab.savedFilterSet?.appliedFilters ?? [];
          const baseFilters = effectiveCustomTab.savedFilterSet?.filters ?? [];

          return (
            buildShipmentFiltersPayload(
              [...baseFilters, ...(filters.filterArray ?? [])] as never,
              [...baseAppliedFilters, ...(filters.appliedFilters ?? [])]
            ) ?? {}
          );
        }

        const rebuiltPayload =
          buildShipmentFiltersPayload(
            (filters.filterArray ?? []) as never,
            filters.appliedFilters ?? []
          ) ?? {};

        if (Object.keys(rebuiltPayload).length > 0) {
          return rebuiltPayload;
        }

        if (
          filters.filtersPayloadRef.current &&
          typeof filters.filtersPayloadRef.current === 'object'
        ) {
          return filters.filtersPayloadRef.current;
        }

        return {};
      })();
      const finalPayload =
        sanitizeProgressPayloadForLeg(effectiveLegType, rawPayload) ?? {};
      const { leg_type: _payloadLegType, ...filterPayload } = finalPayload;

      return {
        page: pageVal,
        page_size: pageSizeVal,
        sort_key: effectiveSortKey,
        sort_order: effectiveSortOrder,
        leg_type: effectiveLegType,
        search: trimmedSearch || '',
        ...(apiTabId ? { tab_id: apiTabId } : {}),
        ...filterPayload,
      };
    },
    [
      activeTabId,
      customTabs,
      filters.appliedFilters,
      filters.filterArray,
      filters.filtersPayloadRef,
      activeLegType,
      sanitizeProgressPayloadForLeg,
      sortKey,
      sortOrder,
    ]
  );

  const shipmentReportPayloadRef = useRef<Record<string, unknown> | null>(null);
  const fetchShipments = useCallback(
    (
      searchquery: string,
      check: boolean,
      _pageArg: number = 1,
      pageSizeArg: number = SHIPMENT_PAGE_SIZE,
      _calledFrom?: string,
      useMissingIds: boolean = false,
      missingIdsOverride?: string[] | null,
      payloadOverride?: ShipmentFiltersPayload | null,
      tabIdOverride?: string | null,
      sortKeyOverride?: string,
      sortOrderOverride?: 'ascending' | 'descending',
      legTypeOverride?: 'CL' | 'EL'
    ): Promise<void> => {
      if (isCalendarActiveRef.current) {
        return Promise.resolve();
      }

      const effectiveLegType = legTypeOverride ?? activeLegType;
      const effectivePayloadOverride =
        payloadOverride === undefined
          ? undefined
          : sanitizeProgressPayloadForLeg(effectiveLegType, payloadOverride);

      const body = buildShipmentRequestBody(
        searchquery,
        1,
        pageSizeArg,
        effectivePayloadOverride,
        tabIdOverride,
        sortKeyOverride,
        sortOrderOverride,
        effectiveLegType
      );

      const finalIds =
        useMissingIds && missingIdsOverride?.length ? missingIdsOverride : [];

      const payload: ShipmentRequestBody & {
        shipments?: string[];
      } = {
        ...body,
        ...(finalIds.length > 0 ? { shipments: finalIds } : {}),
      };

      const getReportQueryPayload = (
        value: Record<string, unknown>
      ): Record<string, unknown> => {
        const { page: _page, ...queryPayload } = value;
        return queryPayload;
      };
      const previousPayload = shipmentReportPayloadRef.current;
      const previousQueryPayload = previousPayload
        ? JSON.stringify(getReportQueryPayload(previousPayload))
        : null;
      const nextQueryPayload = JSON.stringify(getReportQueryPayload(payload));

      setResolvedShipmentPayloadKey(null);
      setResolvedShipmentTotal(null);
      shipmentReportPayloadRef.current = payload;
      setShipmentReportPayload(payload);

      if (previousQueryPayload === nextQueryPayload) {
        if (isShipmentReportRequestInFlightRef.current) {
          return Promise.resolve();
        }

        return queryClient
          .refetchQueries({
            queryKey: ['shipment-report', nextQueryPayload],
            type: 'active',
          })
          .then((): void => {});
      }

      return Promise.resolve();
    },
    [
      activeLegType,
      buildShipmentRequestBody,
      queryClient,
      sanitizeProgressPayloadForLeg,
    ]
  );
  const pendingTabSortFetchRef = useRef<{
    tabId: string;
    sortKey: string;
    sortOrder: 'ascending' | 'descending';
  } | null>(null);
  const suppressNextSortEffectFetchRef = useRef(false);

  useEffect(() => {
    if (!shipmentTabsLoaded) return;
    if (!didRunInitialFetchRef.current) return;

    if (suppressNextSortEffectFetchRef.current) {
      suppressNextSortEffectFetchRef.current = false;
      return;
    }

    const pendingTabSortFetch = pendingTabSortFetchRef.current;
    if (pendingTabSortFetch?.tabId === activeTabId) {
      if (
        sortKey === pendingTabSortFetch.sortKey &&
        sortOrder === pendingTabSortFetch.sortOrder
      ) {
        pendingTabSortFetchRef.current = null;
      }
      return;
    }

    if (!shipmentTabsReadyForFetch && !pendingSortTabsRefreshRef.current) {
      return;
    }

    pageRef.current = 1;
    setPage(1);

    const _ = fetchShipments(
      filters.appliedSearchQuery ?? '',
      true,
      1,
      pageSize
    );
  }, [
    fetchShipments,
    filters.appliedSearchQuery,
    pageSize,
    setPage,
    sortKey,
    sortOrder,
    activeTabId,
    shipmentTabsLoaded,
    shipmentTabsReadyForFetch,
  ]);

  const loadedShipmentRows = useMemo(() => {
    return (shipmentReport?.pages ?? []).flatMap(
      (reportPage) => reportPage.rows ?? []
    );
  }, [shipmentReport]);

  // new shipment creation 1st row animation
  const createdShipmentRowIndex = useMemo(
    () =>
      recentlyCreatedShipmentId
        ? loadedShipmentRows.findIndex(
            (row) => row?.id === recentlyCreatedShipmentId
          )
        : -1,
    [loadedShipmentRows, recentlyCreatedShipmentId]
  );

  // Pins the created shipment for 30s when not in the loaded pages.
  // Re-evaluates if its visibility changes.
  const isCreatedShipmentRowMissing =
    Boolean(recentlyCreatedShipmentId) && createdShipmentRowIndex < 0;

  const loadedRowsWithNewShipmentFirst = useMemo(() => {
    if (createdShipmentRowIndex <= 0) return loadedShipmentRows;

    const nextRows = [...loadedShipmentRows];
    const [createdShipmentRow] = nextRows.splice(createdShipmentRowIndex, 1);
    if (!createdShipmentRow) return loadedShipmentRows;

    return [createdShipmentRow, ...nextRows];
  }, [createdShipmentRowIndex, loadedShipmentRows]);

  useEffect(() => {
    const firstPage = shipmentReport?.pages[0];
    if (!firstPage) return;

    lastShipmentsFetchRef.current = Date.now();
    appendingPageRef.current = false;
    setResolvedShipmentPayloadKey(
      shipmentReportPayload ? JSON.stringify(shipmentReportPayload) : null
    );

    if (!firstPage.countPending) {
      setResolvedShipmentTotal(firstPage.total ?? 0);
      setTotalShipments(firstPage.total ?? 0);
      setOverAllShipmentCount(
        firstPage.overAllShipmentCount ?? firstPage.total ?? 0
      );
    }
    // When count is pending, totals stay at their previous value (or 0 on
    // first load) until the count query resolves below.

    setTotalPages(firstPage.totalPages ?? 1);
    setReportHeaders((previousHeaders) => {
      const nextHeaders = pendingColumnHeadersRef.current
        ? pendingColumnHeadersRef.current
        : normalizeShipmentHeaders(firstPage.headers ?? [], previousHeaders);

      return nextHeaders;
    });
    pendingColumnHeadersRef.current = null;

    if (pendingSortTabsRefreshRef.current) {
      pendingSortTabsRefreshRef.current = false;
      suppressNextSortEffectFetchRef.current = true;
      void refreshShipmentTabs().catch((): void => {});
    }
  }, [
    refreshShipmentTabs,
    shipmentReport,
    shipmentReportPayload,
    setTotalPages,
    setTotalShipments,
  ]);

  // When the deferred count resolves, update the displayed total.
  useEffect(() => {
    if (!shipmentCountData) return;
    setTotalShipments(shipmentCountData.total);
    setOverAllShipmentCount(
      shipmentCountData.overAllShipmentCount ?? shipmentCountData.total
    );
  }, [shipmentCountData, setTotalShipments]);

  const currentShipmentPayloadKey = shipmentReportPayload
    ? JSON.stringify(shipmentReportPayload)
    : null;
  const isWaitingForCurrentShipmentResponse =
    Boolean(currentShipmentPayloadKey) &&
    resolvedShipmentPayloadKey !== currentShipmentPayloadKey;

  const virtualShipmentRows = useMemo(() => {
    const baseRows = filterItemsByLegType(
      loadedRowsWithNewShipmentFirst,
      activeLegType
    );

    // Shipments created via the drawer are always CL — never show the
    // stand-in row while the EL tab is active.
    if (isCreatedShipmentRowMissing && activeLegType === 'CL') {
      // First ~2.5s: skeleton. After that, if the row still hasn't
      // shown up in a loaded page, fall back to the object the create API
      const standInRow = newShipmentSkeletonWindowActive
        ? ({
            [SHIPMENT_PLACEHOLDER_ROW_KEY]: NEW_SHIPMENT_PLACEHOLDER_ROW_KEY,
          } as ShipmentRow)
        : recentlyCreatedShipmentData
          ? ({
              ...recentlyCreatedShipmentData,
              id: recentlyCreatedShipmentId ?? recentlyCreatedShipmentData.id,
            } as ShipmentRow)
          : null;

      if (standInRow) {
        return [standInRow, ...baseRows];
      }
    }

    if (!isFetchingNextPage || baseRows.length === 0) {
      return baseRows;
    }

    const loadingRows = Array.from(
      { length: SHIPMENT_LOADING_ROW_COUNT },
      (_, index) =>
        ({
          [SHIPMENT_PLACEHOLDER_ROW_KEY]: `shipment-loading-row-${index}`,
        }) as ShipmentRow
    );

    return [...baseRows, ...loadingRows];
  }, [
    activeLegType,
    isCreatedShipmentRowMissing,
    isFetchingNextPage,
    loadedRowsWithNewShipmentFirst,
    newShipmentSkeletonWindowActive,
    recentlyCreatedShipmentData,
    recentlyCreatedShipmentId,
  ]);

  const tableLoading =
    (!isCalendarActive &&
      !shipmentReportPayload &&
      loadedShipmentRows.length === 0) ||
    (!isCreatedShipmentRowMissing && isWaitingForCurrentShipmentResponse) ||
    (Boolean(shipmentReportPayload) &&
      !isCreatedShipmentRowMissing &&
      !(isSilentShipmentRefresh && loadedShipmentRows.length > 0) &&
      (isLoadingShipmentReport ||
        (isFetchingShipmentReport && !isFetchingNextPage)));

  useEffect(() => {
    const rowsByLegType = filterItemsByLegType(
      loadedShipmentRows,
      activeLegType
    );
    setFilteredData(rowsByLegType);
    setShipmentsData(rowsByLegType);
  }, [activeLegType, loadedShipmentRows]);

  const effectiveOpenShipmentId = !drawerState.isOpen
    ? null
    : highlightedShipmentId
      ? null
      : drawerState.shipmentId;

  const handleHighlightShipmentCard = (shipmentId: string) => {
    setHighlightedShipmentId(shipmentId);
    toggleDrawer('open', 'Detail', shipmentId);

    const el = document.getElementById(`shipment-card-${shipmentId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const filterByLinkCode = (linkCode: string | null) => {
    if (!linkCode) return;
    const option: AppliedFilter = {
      id: linkCode,
      name: `Group: ${linkCode}`,
      filterBy: 'link_code',
      source: 'manual' as const,
    };

    filters.setAppliedFilters((prevState: AppliedFilter[]) => {
      const alreadyExists = prevState.findIndex(
        (item) => item.id === option.id
      );
      if (alreadyExists === -1) return [...prevState, option];
      return prevState;
    });

    filters.setFilterArray((prev) => {
      const existing = prev.find((f) => f.filterBy === 'link_code');
      if (existing) {
        const existingValues = Array.isArray(existing.filterValue)
          ? existing.filterValue
          : [existing.filterValue];

        if (existingValues.includes(linkCode)) return prev;

        return prev.map((f) =>
          f.filterBy === 'link_code'
            ? { ...f, filterValue: [...existingValues, linkCode] }
            : f
        );
      }

      return [
        ...prev,
        {
          filterBy: 'link_code',
          filterValue: [linkCode],
          source: 'manual' as const,
        },
      ];
    });
  };

  const handleBulkUpload = () => {
    setShowBulkUploadModal(true);
  };

  const handleShipmentFormModal = () => {
    setOpenCreateShipmentDrawer(true);
  };

  const handleBookShipment = () => {
    setBookPrefillShipment(undefined);
    setOpenBookShipmentDrawer(true);
  };

  const handleQuoteShipment = () => {
    setQuoteDrawerMode('quote');
    setQuotePrefillShipment(undefined);
    setOpenQuoteShipmentDrawer(true);
  };

  const handleCreateShellShipment = () => {
    setOpenCreateShellDrawer(true);
  };

  const handleQuoteFromShipment = (shipment: SingleShipmentDataExtended) => {
    setQuoteDrawerMode('quote');
    setQuotePrefillShipment(shipment);
    setOpenQuoteShipmentDrawer(true);
  };

  const handleGenerateReturnLabel = (shipment: SingleShipmentDataExtended) => {
    setQuoteDrawerMode('return');
    setQuotePrefillShipment(shipment);
    setOpenQuoteShipmentDrawer(true);
  };

  const handleBookFromShipment = (shipment: SingleShipmentDataExtended) => {
    setBookPrefillShipment(shipment);
    setOpenBookShipmentDrawer(true);
  };

  const handleConnectButtonClick = () => {
    const _ = navigate('/settings/couriers');
  };

  const onEditShipmentSuccess = () => {
    const _ = fetchShipments(
      filters.appliedSearchQuery ?? '',
      false,
      page,
      pageSize
    );
  };

  const getSummaryTabId = useCallback(
    (tabId: string = activeTabId): string => {
      const customTab = customTabs.find((tab) => tab.id === tabId);
      return customTab?.savedFilterSet?.id || tabId;
    },
    [activeTabId, customTabs]
  );

  const fetchShipmentSummary = useCallback(
    (_runSpin = false, tabId?: string) => {
      return GetShipmentSummary(token, getSummaryTabId(tabId), 'CL')
        .then((response) => {
          const apiData = (response as SummaryApiResponse).data;
          const envelope = apiData?.data;

          if (envelope?.is_success) {
            const payload = envelope.data as SummaryData | undefined;
            if (!payload) return;

            const serialized = JSON.stringify(payload);
            if (serialized !== lastSummaryPayloadRef.current) {
              lastSummaryPayloadRef.current = serialized;
              setSummaryData(payload);
            }

            lastSummaryFetchRef.current = Date.now();
          } else if (envelope?.message) {
            message.error(envelope.message);
          }
        })
        .catch((error) => {
          unauthorize(error);
        });
    },
    [getSummaryTabId, token, unauthorize]
  );

  fetchRef.current = fetchShipments;

  const resetShipmentReportToFirstPage = useCallback(async () => {
    pageRef.current = 1;
    setPage(1);
    await queryClient.resetQueries({ queryKey: ['shipment-report'] });
  }, [queryClient, setPage]);

  const refreshShipmentReportWithoutJump = useCallback(
    async (silent = false) => {
      const loadedPageCount = shipmentReport?.pages.length ?? 0;

      if (loadedPageCount > 1) return;

      if (!silent) {
        await resetShipmentReportToFirstPage();
        return;
      }

      setIsSilentShipmentRefresh(true);
      try {
        await queryClient.refetchQueries({
          queryKey: ['shipment-report'],
          type: 'active',
        });
      } finally {
        setIsSilentShipmentRefresh(false);
      }
    },
    [queryClient, resetShipmentReportToFirstPage, shipmentReport?.pages.length]
  );

  const refreshShipmentsTick = useCallback(async () => {
    if (!isTabVisibleRef.current) return;
    if (isCalendarActiveRef.current) return;
    const now = Date.now();
    const forceRefresh =
      lastShipmentsFetchRef.current === 0 ||
      now - lastShipmentsFetchRef.current >= LIST_FORCE_REFRESH_MS;

    if (forceRefresh) {
      if (shipmentReportPayload) {
        await refreshShipmentReportWithoutJump(true);
      }
      return;
    }

    try {
      // GetShipmentsVersion is typed Promise<unknown>; the endpoint returns this shape
      const res = (await GetShipmentsVersion(
        token,
        shipmentsVersionRef.current ?? undefined
      )) as ShipmentsVersionApiResponse;

      const payload = res?.data?.data;
      const changed = payload?.changed;
      const versionVal = payload?.version;

      if (typeof versionVal === 'number') {
        shipmentsVersionRef.current = versionVal;
      }

      if (changed === true || changed === undefined) {
        if (shipmentReportPayload) {
          await refreshShipmentReportWithoutJump(true);
        }
        if (typeof versionVal === 'number') {
          shipmentsVersionRef.current = versionVal;
        }
      }
    } catch {
      if (shipmentReportPayload) {
        await refreshShipmentReportWithoutJump(true);
      }
    }
  }, [token, refreshShipmentReportWithoutJump, shipmentReportPayload]);

  const refreshSummaryTick = useCallback(async () => {
    if (!isTabVisibleRef.current) return;
    // Throttle. `lastSummaryFetchRef` was written after every successful
    // fetch but never read, so this poll path had no floor at all: measured
    // against production, GET /api/shipment/summary/ was firing every ~2s
    // (2000ms apart, dead regular) even though LIST_POLL_INTERVAL_MS is
    // 60000. Whatever is driving the tick more often than configured, a
    // background refresh never needs to run faster than the poll interval.
    // Forced refreshes (initial load, tab change) call fetchShipmentSummary
    // directly and are deliberately not throttled here.
    const since = Date.now() - lastSummaryFetchRef.current;
    if (lastSummaryFetchRef.current !== 0 && since < LIST_POLL_INTERVAL_MS) {
      return;
    }
    await fetchShipmentSummary(false);
  }, [fetchShipmentSummary]);

  const shipmentSuccess = useCallback(() => {
    setPage(1);
    const _ = fetchShipments(filters.appliedSearchQuery ?? '', true, 1);
    const _summary = fetchShipmentSummary(true);
  }, [
    filters.appliedSearchQuery,
    fetchShipments,
    fetchShipmentSummary,
    setPage,
  ]);

  const toggleDrawer: ToggleDrawerFn = useCallback(
    (state, name, shipmentId, alert, nextLegType) => {
      const isOpening = state === 'open';

      setDrawerState({
        isOpen: isOpening,
        name,
        shipmentId: isOpening ? shipmentId : null,
        ...(alert ? { alert } : { alert: null }),
      });

      setSelectedShipmentID(isOpening ? shipmentId : null);
      if (nextLegType !== undefined) {
        setDrawerLegType(nextLegType);
      } else if (!isOpening) {
        setDrawerLegType(undefined);
      }

      if (!isOpening) {
        setHighlightedShipmentId(null);

        if (returnToMoreDrawerRef.current) {
          returnToMoreDrawerRef.current = false;
          setMoreDrawerOpen(true);
        }
      }
    },
    []
  );

  const openShipmentDrawer = useCallback(
    (shipmentId: string, shipment: ShipmentRow) => {
      toggleDrawer('open', 'Detail', shipmentId, undefined, shipment.leg_type);
    },
    [toggleDrawer]
  );

  const closeDeleteModal = useCallback(() => {
    setArchiveShipmentModalOpen(false);
    setTableActionShipmentID(null);
    shipmentSuccess();
  }, [shipmentSuccess]);

  const handleMarkAsDelivered = useCallback(() => {
    setShowDeliveredModal(false);
    setSelectedShipmentForDelivered(null);
    shipmentSuccess();
  }, [shipmentSuccess]);

  const handleShipmentTableAction = useCallback(
    (
      key: ShipmentTableActionKey,
      shipment: ShipmentRow,
      meta?: {
        CoC_ID?: string | null;
        mentionsMe?: boolean;
        scrollToCommentsBottom?: boolean;
      }
    ) => {
      const shipmentId = shipment?.id || shipment?.shipment_id || null;
      if (!shipmentId) return;

      switch (key) {
        case 'open_comments': {
          const commentsAlert =
            meta?.CoC_ID || meta?.scrollToCommentsBottom
              ? {
                  ...(meta?.CoC_ID ? { CoC_ID: meta.CoC_ID } : {}),
                  ...(meta?.scrollToCommentsBottom
                    ? { scrollToCommentsBottom: true }
                    : {}),
                }
              : null;

          toggleDrawer(
            'open',
            'Detail',
            shipmentId,
            commentsAlert,
            shipment.leg_type
          );
          break;
        }
        case 'edit_shipment':
          toggleDrawer('open', 'Edit', shipmentId, null);
          break;
        case 'setup_notification':
          setTableActionShipmentID(shipmentId);
          setOpenNotificationModal(true);
          setNotificationType(1);
          break;
        case 'dismiss_alerts':
          setShipmentIdToDismiss(shipmentId);
          setDismissAlertsModalVisible(true);
          break;
        case 'map_full_screen':
          navigateToExternalMapView(
            navigate,
            shipmentId,
            location.search,
            shipment?.leg_type
          );
          break;
        case 'product_list':
          toggleDrawer(
            'open',
            'shipmentProductsList',
            shipmentId,
            null,
            shipment?.leg_type
          );
          break;
        case 'share_tracking_link':
          setTableActionShipmentID(shipmentId);
          setShareShipmentModalOpen(true);
          break;
        case 'archive_shipment':
          setTableActionShipmentID(shipmentId);
          setArchiveShipmentModalOpen(true);
          break;
        case 'schedule_pickup':
          toggleDrawer(
            'open',
            'SchedulePickup',
            shipmentId,
            null,
            shipment.leg_type
          );
          break;
        case 'mark_as_delivered':
          setSelectedShipmentForDelivered(shipment);
          setShowDeliveredModal(true);
          break;
        case 'export_coc':
          // Handled by ExportCOCWrapper in the table cell
          break;
        case 'one_time_message':
          setTableActionShipmentID(shipmentId);
          setOpenNotificationModal(true);
          setNotificationType(2);
          break;
        default:
          toggleDrawer('open', 'Detail', shipmentId, null, shipment.leg_type);
          break;
      }
    },
    [location.search, navigate, toggleDrawer]
  );

  const handleConfirmDismiss = useCallback(() => {
    if (!shipmentIdToDismiss) return;

    handleMassAction({
      action: 'dismiss_all_alerts',
      shipmentIds: [shipmentIdToDismiss],
      token,
      successMessage: 'Status updated',
      errorMessage: 'Dismiss failed',
      entityLabel: 'shipment',
      selectAll: false,
      filtersPayload: {},
      search: '',
      excludeShipmentIds: [],
      countForUndo: 1,
      onSuccess: () => {
        // Refresh shipments data
        const _ = queryClient.invalidateQueries({ queryKey: ['shipments'] });
        setDismissAlertsModalVisible(false);
        setShipmentIdToDismiss(null);
      },
    });
  }, [shipmentIdToDismiss, handleMassAction, token, queryClient]);

  const hasMoreShipments = activeViewType === 'list' && Boolean(hasNextPage);

  const handleLoadMoreShipments = useCallback(() => {
    if (appendingPageRef.current || isFetchingNextPage || !hasMoreShipments) {
      return;
    }

    const nextPage = pageRef.current + 1;
    appendingPageRef.current = true;
    pageRef.current = nextPage;
    setPage(nextPage);

    const _ = fetchNextPage({ cancelRefetch: false }).finally(() => {
      appendingPageRef.current = false;
    });
  }, [fetchNextPage, hasMoreShipments, isFetchingNextPage, setPage]);

  useEffect(() => {
    if (shipmentNav?.drawerState === 'open' && shipmentNav?.shipmentId) {
      toggleDrawer('open', 'Detail', shipmentNav.shipmentId, {
        CoC_ID: shipmentNav.CoC_ID,
      });

      dispatch(clearShipmentNav());
    }
  }, [
    shipmentNav?.drawerState,
    shipmentNav?.shipmentId,
    shipmentNav?.CoC_ID,
    toggleDrawer,
    dispatch,
  ]);

  const handleCloseDrawer = () => {
    setOpenCreateShipmentDrawer(false);

    form.resetFields();
  };

  const handleCreateShipmentSuccess = useCallback(
    (shipment: unknown) => {
      const createdShipment = (shipment ?? {}) as ShipmentRow;
      setRecentlyCreatedShipmentId(createdShipment.id ?? null);
      setRecentlyCreatedShipmentData(createdShipment);
      setNewShipmentSkeletonWindowActive(true);

      // Shipments created via this drawer are always CL — if the user was
      // sitting on the EL tab, jump them to CL so the new row is visible.
      if (activeLegType !== 'CL') {
        setActiveLegType('CL');
        pageRef.current = 1;
        setPage(1);
      }

      setTimeout(() => {
        setNewShipmentSkeletonWindowActive(false);
      }, 5000);

      setTimeout(() => {
        setRecentlyCreatedShipmentId(null);
        setRecentlyCreatedShipmentData(null);
      }, 30000);

      // Force leg_type: 'CL' explicitly rather than relying on activeLegType
      // — the setActiveLegType above hasn't applied to this closure yet.
      const _ = fetchShipments(
        filters.appliedSearchQuery ?? '',
        true,
        page,
        pageSize,
        undefined,
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        'CL'
      );
    },
    [
      activeLegType,
      fetchShipments,
      filters.appliedSearchQuery,
      page,
      pageSize,
      setActiveLegType,
      setPage,
    ]
  );

  useEffect(() => {
    const onCloseOverlays = () => {
      toggleDrawer('close', '', null, null);
      setMoreDrawerOpen(false);
      moreDrawerPageRef.current = 1;
      setOpenCreateShipmentDrawer(false);
      setShowBulkUploadModal(false);
    };
    window.addEventListener(ZOOMLOGI_CLOSE_UI_OVERLAYS_EVENT, onCloseOverlays);
    return () =>
      window.removeEventListener(
        ZOOMLOGI_CLOSE_UI_OVERLAYS_EVENT,
        onCloseOverlays
      );
  }, [toggleDrawer]);

  const handleRunSearchFromPanel = useCallback(
    ({
      query,
      check,
      pageSize: ps,
      calledFrom,
      payload,
    }: {
      query: string;
      check: boolean;
      page?: number;
      pageSize?: number;
      calledFrom?: string;
      payload?: ShipmentFiltersPayload | null;
    }) => {
      const trimmed = (query ?? '').trim();

      if (calledFrom === 'search') {
        setAppliedSearchQuery(trimmed);
      }

      suppressNextSortEffectFetchRef.current = true;
      setPage(1);
      pageRef.current = 1;

      const _ = fetchShipments(
        trimmed,
        check,
        1,
        ps ?? pageSize,
        undefined,
        false,
        null,
        payload ?? null
      );
    },
    [fetchShipments, pageSize, setAppliedSearchQuery, setPage]
  );

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  const applyQuickFilter = useCallback(
    ({
      filterBy,
      groupTitle,
      items,
      append = true,
      filterValueOverride,
    }: {
      filterBy: string;
      groupTitle?: string;
      items: Array<{ id: string; name: string; title?: string }>;
      append?: boolean;
      filterValueOverride?: string[];
    }) => {
      const idsForArray = filterValueOverride ?? items.map((x) => x.id);

      filters.setAppliedFilters((prev) => {
        const next = append
          ? [...prev]
          : prev.filter((p) => p.filterBy !== filterBy);

        const toAdd = items
          .filter(
            (it) => !next.some((p) => p.filterBy === filterBy && p.id === it.id)
          )
          .map((it) => ({
            id: it.id,
            name: it.name,
            title: it.title ?? it.name,
            filterBy,
            groupTitle: groupTitle ?? '',
            source: 'manual' as const,
          }));

        return [...next, ...toAdd];
      });

      filters.setFilterArray((prev) => {
        const next = append
          ? [...prev]
          : prev.filter((f) => f.filterBy !== filterBy);

        return [
          ...next.filter((f) => f.filterBy !== filterBy),
          { filterBy, filterValue: idsForArray, source: 'manual' as const },
        ];
      });

      const payloadOverride = {
        ...(filters.filtersPayloadRef.current ?? {}),
        ...(filterBy === 'missing_pod'
          ? { missing_pod: filterValueOverride ?? [] }
          : { [filterBy]: idsForArray }),
      };

      pageRef.current = 1;
      suppressNextSortEffectFetchRef.current = true;
      setPage(1);

      const _ = fetchShipments(
        filters.appliedSearchQuery ?? '',
        true,
        1,
        pageSize,
        undefined,
        false,
        null,
        payloadOverride
      );
    },
    [filters, fetchShipments, pageSize, setPage]
  );

  useEffect(() => {
    searchQueryRef.current = (filters.appliedSearchQuery ?? '').trim();
  }, [filters.appliedSearchQuery]);

  useEffect(() => {
    searchVisibleRef.current = filters.isSearchVisible;
  }, [filters.isSearchVisible]);

  useShipmentsEffects({
    location,
    drawerStateFromHome,
    setDrawerStateFormHome,
    page,
    pageSize,
    isSearchVisible: filters.isSearchVisible,
    searchQuery: filters.searchQuery,
    startAlertsInterval,
    pollIntervalMs: LIST_POLL_INTERVAL_MS,
    fetchShipments,
    fetchShipmentSummary,
    refreshShipmentsTick,
    refreshSummaryTick,
    drawerState,
    setDrawerState,
    toggleDrawer,
    pageRef,
    pageSizeRef,
    isTabVisibleRef,
    searchVisibleRef,
    searchQueryRef,
    didRunInitialFetchRef,
    didInitialQueryStringEffect,
    fetchRef,
    initialFetchEnabled: shipmentTabsReadyForFetch,
  });

  // useShipmentsEffects' own shipment_id-from-URL effect (shared with
  // LegacyShipments) always opens the drawer with a null alert
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shipmentId = params.get('shipment_id');
    if (!shipmentId) return;
    const highlight = params.get('highlight');
    const routerState = location.state as {
      __zl_from_activity?: boolean;
      __zl_open_shipment_notifications?: boolean;
      __zl_open_shipment_notification?: string | null;
    } | null;
    const isFromActivity = Boolean(routerState?.__zl_from_activity);
    if (!highlight && !isFromActivity) return;

    // A shipment-notification activity row opens the notifications drawer,
    // under the same checks as the bell in ShipmentHeader; else Detail.
    const notificationId =
      routerState?.__zl_open_shipment_notification || undefined;
    const canOpenNotifications =
      Boolean(routerState?.__zl_open_shipment_notifications) &&
      Boolean(allPermissions?.is_view) &&
      getLegTypeFromSearch(location.search) !== 'EL';

    setDrawerState((prev) =>
      prev.shipmentId === shipmentId && !prev.alert
        ? {
            ...prev,
            ...(canOpenNotifications && { name: 'notifications' }),
            alert: {
              CoC_ID: highlight || undefined,
              isFromActivityFeed: isFromActivity,
              ...(canOpenNotifications && { notificationId }),
            },
          }
        : prev
    );
  }, [
    location.search,
    location.state,
    setDrawerState,
    allPermissions?.is_view,
  ]);

  // Keep the canonical /shipment?shipment_id=... URL shareable for as long
  // as an Activity-feed-originated drawer stays open — ShipmentDetailDrawerWithTeams's
  useEffect(() => {
    const alert = drawerState.alert as {
      CoC_ID?: string;
      isFromActivityFeed?: boolean;
    } | null;
    if (
      !drawerState.isOpen ||
      !alert?.isFromActivityFeed ||
      !drawerState.shipmentId
    ) {
      return;
    }
    const params = new URLSearchParams(location.search);
    if (params.get('shipment_id') === drawerState.shipmentId) return;

    const next = new URLSearchParams();
    next.set('shipment_id', drawerState.shipmentId);
    next.set('leg_type', drawerLegType || 'CL');
    if (alert.CoC_ID) next.set('highlight', alert.CoC_ID);
    // Keep the marker in state, not the URL, so a re-run still sees it.
    const _ = navigate(`${location.pathname}?${next.toString()}`, {
      replace: true,
      state: { __zl_from_activity: true },
    });
  }, [
    drawerState.isOpen,
    drawerState.alert,
    drawerState.shipmentId,
    drawerLegType,
    location.pathname,
    location.search,
    navigate,
  ]);

  const filtersKey = useMemo(() => {
    return JSON.stringify({
      applied: (filters.appliedFilters ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        title: f.title,
        filterBy: f.filterBy,
        groupTitle: f.groupTitle,
      })),
    });
  }, [filters.appliedFilters]);

  const calendarSearchQuery = useMemo(() => {
    return isSearchTriggered ? (filters.appliedSearchQuery ?? '').trim() : '';
  }, [filters.appliedSearchQuery, isSearchTriggered]);
  const payloadHasFilters = (() => {
    const payload = filters.filtersPayloadRef?.current;

    if (!payload || typeof payload !== 'object') return false;

    return Object.entries(payload).some(([key, value]) => {
      if (key === 'tab_id') return false;
      if (key === 'search') return false;
      if (key === 'page') return false;
      if (key === 'page_size') return false;

      if (Array.isArray(value)) return value.length > 0;
      if (value && typeof value === 'object')
        return Object.keys(value).length > 0;

      return Boolean(value);
    });
  })();

  const hasActiveFilters = useMemo(() => {
    const isCustomTabActive =
      activeTabId !== 'all_shipments' && activeTabId !== 'active';

    return (
      isCustomTabActive ||
      isSearchTriggered ||
      (filters.appliedFilters?.length ?? 0) > 0 ||
      (filters.filterArray?.length ?? 0) > 0 ||
      payloadHasFilters
    );
  }, [
    activeTabId,
    isSearchTriggered,
    filters.appliedFilters,
    filters.filterArray,
    payloadHasFilters,
  ]);
  const handleCalendarFetchSuccess = useCallback(() => {
    if (pendingSortTabsRefreshRef.current) {
      pendingSortTabsRefreshRef.current = false;
      void refreshShipmentTabs().catch((): void => {});
    }
  }, [refreshShipmentTabs]);

  const {
    calendarData,
    calendarCounts,
    fetchCalendarShipments,
    viewBy,
    setViewBy,
    calendarTotalShipments,
  } = useCalendarShipments({
    token,
    unauthorize: handleUnauthorized,
    legType: activeLegType,
    filtersPayloadRef: filters.filtersPayloadRef,
    activeTabId,
    sortKey,
    sortOrder,
    enabled: isCalendarActive,
    filtersKey,
    hasActiveFilters,
    searchQuery: calendarSearchQuery,
    onFetchSuccess: handleCalendarFetchSuccess,
  });

  const handleTabChange = useCallback(
    (tabId: string, tabOverride?: ShipmentTab) => {
      dispatch(setActiveTabId(tabId));

      const customTab =
        tabOverride ?? customTabs.find((tab) => tab.id === tabId);
      const selectedTab =
        customTab ?? defaultTabs.find((tab) => tab.id === tabId);
      const tabSortKey = selectedTab?.sort_key ?? 'scheduled_delivery_date';
      const tabSortOrder = selectedTab?.sort_order ?? 'ascending';
      pendingTabSortFetchRef.current = {
        tabId,
        sortKey: tabSortKey,
        sortOrder: tabSortOrder,
      };
      lastSummaryPayloadRef.current = null;
      const _summary = fetchShipmentSummary(true, tabId);

      if (customTab) {
        const savedLegType = customTab.savedFilterSet?.legType ?? 'CL';
        if (savedLegType !== activeLegType) {
          setActiveLegType(savedLegType);
        }
        const baseAppliedFilters: AppliedFilter[] = (
          customTab.savedFilterSet?.appliedFilters ?? []
        ).map((filter) => ({
          ...filter,
          source: 'tab_base' as const,
        }));

        const baseFilters: FilterArrayItem[] = (
          customTab.savedFilterSet?.filters ?? []
        ).map((filter) => ({
          ...filter,
          source: 'tab_base' as const,
        }));

        const visibleAdditionalApplied: AppliedFilter[] = (
          additionalFiltersPerTab[tabId] ?? []
        ).map((filter) => ({
          ...filter,
          source: 'manual' as const,
        }));

        const visibleAdditionalArray: FilterArrayItem[] = (
          additionalFiltersPerTab[tabId] ?? []
        ).map((filter) => {
          const rawValue = (
            filter as {
              filterBy?: string;
              filterValue?: ID[] | string | number;
            }
          ).filterValue;

          return {
            filterBy: (filter as { filterBy?: string }).filterBy ?? '',
            filterValue:
              Array.isArray(rawValue) ||
              typeof rawValue === 'string' ||
              typeof rawValue === 'number'
                ? rawValue
                : [],
            source: 'manual' as const,
          };
        });
        const sanitizedFilters = sanitizeProgressFiltersForLeg(savedLegType, {
          appliedFilters: [...baseAppliedFilters, ...visibleAdditionalApplied],
          filterArray: [...baseFilters, ...visibleAdditionalArray],
        });
        const sanitizedVisibleFilters = sanitizeProgressFiltersForLeg(
          savedLegType,
          {
            appliedFilters: visibleAdditionalApplied,
            filterArray: visibleAdditionalArray,
          }
        );
        const emptyProgressSnapshot: {
          appliedFilters: AppliedFilter[];
          filterArray: FilterArrayItem[];
        } = {
          appliedFilters: [],
          filterArray: [],
        };

        filters.setProgressFiltersByLeg({
          CL:
            savedLegType === 'CL'
              ? sanitizedVisibleFilters
              : emptyProgressSnapshot,
          EL:
            savedLegType === 'EL'
              ? sanitizedVisibleFilters
              : emptyProgressSnapshot,
        });

        setAppliedFilters(sanitizedVisibleFilters.appliedFilters);
        setFilterArray(sanitizedVisibleFilters.filterArray as never);

        const payload = buildShipmentFiltersPayload(
          sanitizedFilters.filterArray as never,
          sanitizedFilters.appliedFilters
        );

        filters.setFiltersPayload(payload);
        pageRef.current = 1;
        setPage(1);

        if (isCalendarActiveRef.current) {
          const _ = fetchCalendarShipments(
            undefined,
            payload,
            calendarRangeRef.current ?? undefined,
            true,
            calendarViewTypeRef.current,
            tabId,
            tabSortKey,
            tabSortOrder,
            savedLegType
          );
          return;
        }

        const _ = fetchRef.current(
          filters.appliedSearchQuery ?? '',
          true,
          1,
          pageSize,
          'tab_change',
          false,
          null,
          payload,
          tabId,
          tabSortKey,
          tabSortOrder,
          savedLegType
        );
        return;
      }

      // Default tabs
      filters.setProgressFiltersByLeg({});
      setAppliedFilters([]);
      setFilterArray([]);
      filters.setFiltersPayload(null);
      pageRef.current = 1;
      setPage(1);

      if (isCalendarActiveRef.current) {
        const _ = fetchCalendarShipments(
          undefined,
          null,
          calendarRangeRef.current ?? undefined,
          true,
          calendarViewTypeRef.current,
          tabId,
          tabSortKey,
          tabSortOrder
        );
        return;
      }

      const _refetch = fetchRef.current(
        filters.appliedSearchQuery ?? '',
        true,
        1,
        pageSize,
        'tab_change',
        false,
        null,
        null,
        tabId,
        tabSortKey,
        tabSortOrder
      );
    },
    [
      dispatch,
      customTabs,
      defaultTabs,
      additionalFiltersPerTab,
      setAppliedFilters,
      setFilterArray,
      filters,
      activeLegType,
      setActiveLegType,
      pageSize,
      setPage,
      fetchCalendarShipments,
      fetchShipmentSummary,
      sanitizeProgressFiltersForLeg,
    ]
  );
  const filtersForSavedFilterSetModal = useMemo(() => {
    const customTab = customTabs.find((tab) => tab.id === activeTabId);

    if (!customTab) {
      return {
        currentAppliedFilters: filters.appliedFilters ?? [],
        currentFilterArray: filters.filterArray ?? [],
      };
    }

    // Progress is intentionally taken from live state. It is view-scoped and
    // may differ from the saved set's original view after a CL/EL switch.
    const baseAppliedFilters = withoutProgressAppliedFilters(
      customTab.savedFilterSet?.appliedFilters ?? []
    );
    const baseFilters = withoutProgressFilterArray(
      customTab.savedFilterSet?.filters ?? []
    );

    const appliedSeen = new Set<string>();
    const mergedAppliedFilters: AppliedFilter[] = [];

    [...baseAppliedFilters, ...(filters.appliedFilters ?? [])].forEach((f) => {
      const key = `${String(f.id)}::${f.filterBy ?? ''}::${f.dateFilter ?? ''}`;
      if (!appliedSeen.has(key)) {
        appliedSeen.add(key);
        mergedAppliedFilters.push(f);
      }
    });

    const filterArrayMap = new Map<string, Set<string | number>>();
    const filterArrayMetadata = new Map<
      string,
      Pick<FilterArrayItem, 'logic' | 'source'>
    >();

    [...baseFilters, ...(filters.filterArray ?? [])].forEach((item) => {
      const key = item.filterBy;
      if (!key) return;

      const existing = filterArrayMap.get(key) ?? new Set<string | number>();
      const values = Array.isArray(item.filterValue)
        ? item.filterValue
        : [item.filterValue];

      values.forEach((v) => existing.add(v));
      filterArrayMap.set(key, existing);
      filterArrayMetadata.set(key, {
        ...(item.logic ? { logic: item.logic } : {}),
        ...(item.source ? { source: item.source } : {}),
      });
    });

    const mergedFilterArray = Array.from(filterArrayMap.entries()).map(
      ([filterBy, values]) => ({
        filterBy,
        filterValue: Array.from(values),
        ...filterArrayMetadata.get(filterBy),
      })
    );

    return {
      currentAppliedFilters: mergedAppliedFilters,
      currentFilterArray: mergedFilterArray,
    };
  }, [activeTabId, customTabs, filters.appliedFilters, filters.filterArray]);

  const massActionFiltersPayload = useMemo(() => {
    return buildMassActionFiltersPayload({
      activeTabId,
      appliedFilters: filters.appliedFilters ?? [],
      filterArray: filters.filterArray ?? [],
      customTabs,
      currentPayload: filters.filtersPayloadRef?.current ?? null,
    });
  }, [
    activeTabId,
    customTabs,
    filters.appliedFilters,
    filters.filterArray,
    filters.filtersPayloadRef,
  ]);

  const {
    savedFilterModalOpen,
    editingFilterSet,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSave,
    handleDelete,
  } = useShipmentSavedFilterSetModal({
    token,
    dispatch,
    activeTabId,
    activeLegType,
    onTabChange: handleTabChange,
  });
  const calendarViewport = useCalendarViewportCounts(calendarData ?? []);
  const { handleRangeChange: calendarHandleRangeChange } = calendarViewport;
  const calendarRangeRef = useRef<{ start: string; end: string } | null>(null);
  const calendarViewTypeRef = useRef<string>('dayGridMonth');
  const toolbarFiltersPayload =
    isCalendarActive && calendarRangeRef.current
      ? {
          ...massActionFiltersPayload,
          start: calendarRangeRef.current.start,
          end: calendarRangeRef.current.end,
          view_by: viewBy,
        }
      : massActionFiltersPayload;
  const calendarToolbarTotal = useMemo(() => {
    if (activeViewType !== 'calendar') return 0;

    return calendarTotalShipments ?? 0;
  }, [activeViewType, calendarTotalShipments]);

  const calendarSummaryOverride = useMemo(() => {
    if (activeViewType !== 'calendar') return null;

    return {
      is_critical: calendarCounts?.critical ?? 0,
      is_risk: calendarCounts?.at_risk ?? 0,
      is_track: calendarCounts?.no_issue ?? 0,
    };
  }, [activeViewType, calendarCounts]);

  const shipmentSelectionScopeCount = useMemo(
    () =>
      getSelectionScopeCount({
        activeViewType,
        activeTabId,
        hasActiveFilters,
        isSearchTriggered,
        calendarTotalShipments,
        calendarToolbarTotal,
        totalShipments,
        overAllShipmentCount,
      }),
    [
      activeViewType,
      activeTabId,
      hasActiveFilters,
      isSearchTriggered,
      calendarTotalShipments,
      calendarToolbarTotal,
      totalShipments,
      overAllShipmentCount,
    ]
  );

  const hasToolbarData =
    activeViewType === 'calendar'
      ? (calendarData ?? []).length > 0
      : (filteredData ?? []).length > 0;

  const isCalendarView = activeViewType === 'calendar';
  const hasCalendarData = (calendarData ?? []).length > 0;
  const hasListData = shipmentsData.length > 0;
  const hasAnyData = isCalendarView ? hasCalendarData : hasListData;
  const hasShipmentApiResponseForCurrentPayload =
    Boolean(currentShipmentPayloadKey) &&
    resolvedShipmentPayloadKey === currentShipmentPayloadKey;
  const shouldShowEmptyShipments =
    !isCalendarView &&
    hasShipmentApiResponseForCurrentPayload &&
    resolvedShipmentTotal === 0 &&
    !tableLoading &&
    !hasAnyData &&
    activeTabId === 'all_shipments' &&
    activeLegType === 'CL' &&
    !hasActiveFilters;
  const shouldShowToolbar =
    (hasToolbarData || hasActiveFilters) &&
    allPermissions?.is_view &&
    decodedToken?.company_name?.toLowerCase() !== 'zoomlogi support';
  const shouldShowMainLayout = isCalendarView || !shouldShowEmptyShipments;
  const availableHourSlots = useMemo(() => {
    const slots: string[] = [];

    (calendarData ?? []).forEach((dayAgg) => {
      (dayAgg.hour_aggregates ?? []).forEach((h) => {
        if (Number(h.total ?? 0) > 0 && h.hour) slots.push(String(h.hour));
      });
    });

    return slots.sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
  }, [calendarData]);

  const handleCalendarRangeChange = useCallback(
    (arg: unknown) => {
      calendarHandleRangeChange(
        arg as Parameters<typeof calendarHandleRangeChange>[0]
      );

      const a = arg as {
        startStr?: string;
        endStr?: string;
        viewType?: string;
      };
      if (!a?.startStr || !a?.endStr) return;
      // Track current FullCalendar view type for re-fetches triggered by
      // filter/viewBy changes (those won't come through onRangeChange).
      if (a.viewType) {
        calendarViewTypeRef.current = a.viewType;
      }
      // Normalise to plain YYYY-MM-DD regardless of whether FullCalendar
      // returns a date-only string or a datetime with a timezone offset
      // (e.g. "2025-11-30T00:00:00+05:00").  The backend only needs dates.
      const startDate = a.startStr.slice(0, 10);
      const exclusiveEndDate = a.endStr.slice(0, 10);

      // FullCalendar's end is exclusive — subtract one day to make it inclusive.
      const [y, m, d] = exclusiveEndDate.split('-').map(Number);
      const prev = new Date(Date.UTC(y, m - 1, d - 1));
      const inclusiveEnd = prev.toISOString().slice(0, 10);

      const newRange = { start: startDate, end: inclusiveEnd };
      calendarRangeRef.current = newRange;

      if (!isCalendarActiveRef.current) return;

      const _ = fetchCalendarShipments(
        undefined,
        filters.filtersPayloadRef?.current ?? null,
        newRange,
        undefined,
        a.viewType
      );
    },
    [
      calendarHandleRangeChange,
      fetchCalendarShipments,
      filters.filtersPayloadRef,
    ]
  );

  useEffect(() => {
    if (!isCalendarActive) return;
    const range = calendarRangeRef.current;
    if (!range) return;
    const _ = fetchCalendarShipments(
      viewBy,
      filters.filtersPayloadRef?.current ?? null,
      range,
      undefined,
      calendarViewTypeRef.current
    );
  }, [
    isCalendarActive,
    viewBy,
    activeLegType,
    filtersKey,
    calendarSearchQuery,
    sortKey,
    sortOrder,
    fetchCalendarShipments,
    filters.filtersPayloadRef,
    // NOTE: calendarRangeRef is a ref — range-change fetches are triggered
    // by handleCalendarRangeChange above, not this effect.
  ]);

  const buttonsList = [
    { id: 'list', name: 'List', icon: <ListIcon /> },
    { id: 'calendar', name: 'Calendar', icon: <CalendarIcon /> },
  ];
  const legTypeButtonsList = [
    { id: 'CL', name: 'CL', icon: <span>CL</span> },
    { id: 'EL', name: 'EL', icon: <span>EL</span> },
  ];

  const handleChangeViewType = (val: string) => {
    const nextViewType = val as 'list' | 'calendar';

    if (drawerState.isOpen) {
      toggleDrawer('close', '', null, null);
      setHighlightedShipmentId(null);
    }

    if (nextViewType === 'list') {
      setDrawerSource('list');
    }

    // stale statuses from list leaked into calendar's delivered check
    if (nextViewType !== activeViewType) {
      setSelectedIds([]);
      setSelectedShipmentStatuses({});
      setSelectAllAcrossFilters(false);
      setExcludedIds([]);
    }

    setActiveViewType(nextViewType);

    // Force a fresh, leg-type-correct fetch the moment Calendar is selected,
    // rather than relying on the reactive effect (which only refetches when
    // its own dependency identities change and can otherwise show whatever
    // CL/EL data was last fetched for the calendar, ignoring a toggle that
    // happened while the user was still in List view).
    if (nextViewType === 'calendar') {
      const range = calendarRangeRef.current;
      if (range) {
        const _ = fetchCalendarShipments(
          undefined,
          filters.filtersPayloadRef?.current ?? null,
          range,
          true,
          calendarViewTypeRef.current
        );
      }
    }
  };

  const handleChangeLegType = (val: string) => {
    const nextLegType = val === 'EL' ? 'EL' : 'CL';
    if (nextLegType === activeLegType) return;

    const nextProgressFilters = prepareProgressFiltersForLeg(nextLegType);
    const activeCustomTab = customTabs.find((tab) => tab.id === activeTabId);
    const nextFiltersForPayload =
      activeCustomTab != null
        ? sanitizeProgressFiltersForLeg(nextLegType, {
            filterArray: [
              ...withoutProgressFilterArray(
                activeCustomTab.savedFilterSet?.filters ?? []
              ),
              ...nextProgressFilters.filterArray,
            ],
            appliedFilters: [
              ...withoutProgressAppliedFilters(
                activeCustomTab.savedFilterSet?.appliedFilters ?? []
              ),
              ...nextProgressFilters.appliedFilters,
            ],
          })
        : nextProgressFilters;
    const nextFiltersPayload =
      buildShipmentFiltersPayload(
        nextFiltersForPayload.filterArray as never,
        nextFiltersForPayload.appliedFilters
      ) ?? {};
    filters.setFiltersPayload(nextFiltersPayload);

    if (drawerState.isOpen) {
      toggleDrawer('close', '', null, null);
      setHighlightedShipmentId(null);
    }

    setActiveLegType(nextLegType);

    if (isCalendarActiveRef.current) {
      const range = calendarRangeRef.current;
      if (!range) return;
      const _ = fetchCalendarShipments(
        undefined,
        nextFiltersPayload,
        range,
        true,
        calendarViewTypeRef.current,
        undefined,
        undefined,
        undefined,
        nextLegType
      );
      return;
    }

    pageRef.current = 1;
    setPage(1);
  };

  const didSwitchToListRef = useRef(false);

  useEffect(() => {
    if (isCalendarActive) {
      didSwitchToListRef.current = true;
      return;
    }
    // we just switched from calendar -> list
    if (didSwitchToListRef.current) {
      didSwitchToListRef.current = false;

      const _ = fetchShipments(
        filters.appliedSearchQuery ?? '',
        true,
        pageRef.current,
        pageSizeRef.current
      );
    }
  }, [isCalendarActive, fetchShipments, filters.appliedSearchQuery]);

  const selectableItems = useMemo(() => {
    if (activeViewType === 'calendar') {
      return (calendarData ?? []).flatMap((agg) =>
        agg?.top_shipments?.map((it) => ({
          id: String(it.shipment_id),
          status: it.status,
        }))
      );
    }
    return (filteredData ?? []).map((it) => ({
      id: it.id,
      status: it.status,
    }));
  }, [activeViewType, calendarData, filteredData]);

  const isSelectAllMode =
    activeViewType === 'list' && !!selectAllAcrossFilters && !isCalendarActive;
  const isCalendarSelectAllMode = isCalendarActive && !!selectAllAcrossFilters;
  const shouldShowShipmentMobileCards = activeViewType === 'list' && (xs || sm);
  const shouldCollapseAboveTable =
    activeViewType === 'list' && drawerState.isOpen;
  const shouldHideSort =
    activeViewType === 'list' &&
    drawerState.isOpen &&
    hasActiveFilters &&
    !activeCustomTab;
  const handleDeleteSavedFilterSetFromDropdown = useCallback(
    async (filterSetId: string) => {
      const linkedTab = customTabs.find(
        (tab) => tab.savedFilterSet?.id === filterSetId
      );

      await handleDelete({
        filterSet: { id: filterSetId },
        tabId: linkedTab?.id ?? null,
      });
    },
    [customTabs, handleDelete]
  );

  const handleShipmentColumnsChange = useCallback((headers: HeaderItem[]) => {
    const orderedHeaders = normalizeShipmentHeaders(
      headers,
      reportHeadersRef.current
    );

    pendingColumnHeadersRef.current = orderedHeaders;
    setColumnConfigHeaders(orderedHeaders);
    setReportHeaders(orderedHeaders);
  }, []);

  const effectiveReportHeaders =
    columnConfigHeaders.length > 0
      ? columnConfigHeaders
      : reportHeaders.length > 0
        ? reportHeaders
        : SKELETON_HEADERS;

  return (
    <div
      className='shipment-content-viewport'
      data-detail-open={shouldCollapseAboveTable ? 'true' : 'false'}
    >
      <div
        className={`${
          drawerSource === 'calendar'
            ? 'shipment-section-calendar'
            : 'shipment-section shipment-section-table'
        }`}
      >
        <div
          className={`${
            activeViewType === 'calendar'
              ? 'header-sticky'
              : 'shipment-tabs-only-header'
          } shipment-above-table-content${
            shouldCollapseAboveTable ? ' open' : ''
          }`}
        >
          <DashboardTitle
            title={t('title')}
            showAddButton={!hideNewShipmentButton}
            showTrack={manualShipmentFlags.track}
            onAddButtonClick={handleShipmentFormModal}
            onBulkUpload={handleBulkUpload}
            onBookShipment={
              manualShipmentFlags.book ? handleBookShipment : undefined
            }
            onQuoteShipment={
              manualShipmentFlags.quote ? handleQuoteShipment : undefined
            }
            onCreateShellShipment={
              manualShipmentFlags.create ? handleCreateShellShipment : undefined
            }
            bookShipmentMenuDisabled={bookShipmentMenuDisabled}
            allPermissions={allPermissions}
            tabsSlot={
              <TabStrip
                tableView={true}
                permissions={permissions}
                token={token}
                activeTabCount={
                  firstPageCountPending && shipmentCountData == null
                    ? undefined
                    : (totalShipments ?? 0)
                }
                onTabChange={handleTabChange}
                currentAppliedFilters={filters.appliedFilters}
                currentFilterArray={filters.filterArray}
                onOpenCreateModal={openCreateModal}
                onOpenEditModal={openEditModal}
                onOpenViewFiltersModal={handleOpenViewFiltersModal}
              />
            }
            switchViewButtons={
              <Flex gap={8}>
                {hasOperationalLegView && (
                  <SwitchButtons
                    options={legTypeButtonsList}
                    defaultActive={activeLegType}
                    onChange={handleChangeLegType}
                  />
                )}
                <SwitchButtons
                  isIcons={true}
                  options={buttonsList}
                  defaultActive={activeViewType}
                  onChange={handleChangeViewType}
                />
              </Flex>
            }
            tableView={true}
          />
        </div>
        <div className='sm-14-space' />
        {shouldShowEmptyShipments && (
          <EmptyShipments
            handleConnectButtonClick={handleConnectButtonClick}
            handleShipmentFormModal={handleShipmentFormModal}
            allPermissions={allPermissions}
            showTrackManually={manualShipmentFlags.track}
            isLoading={false}
          />
        )}

        {shouldShowMainLayout && (
          <>
            <Flex
              style={{
                overflowX: 'hidden',
                display: 'block',
                flex: '1 1 auto',
                minHeight: 0,
              }}
            >
              <div className='shipment-list-layout'>
                <div
                  className={`shipment-above-table-content${
                    shouldCollapseAboveTable ? ' open' : ''
                  }`}
                >
                  <SummaryCards
                    isCalendarView={activeViewType === 'calendar'}
                    calendarSummaryOverride={calendarSummaryOverride}
                    drawerSource={drawerSource}
                    summaryData={summaryData}
                    isSupportCompany={isSupportCompany}
                    panelApplyFilters={filters.runPanelApplyFilters}
                    drawerOpen={shouldCollapseAboveTable}
                    tableView={true}
                  />

                  <Flex
                    className={` ${
                      shouldCollapseAboveTable
                        ? 'shipment-toolbar-alignment'
                        : ''
                    }`}
                    align='center'
                    justify='space-between'
                    gap={16}
                  >
                    <Flex
                      align='center'
                      style={{ flex: 1, minWidth: 0 }}
                      className='relative shipment-toolbar-gap'
                    >
                      {shouldShowToolbar && (
                        <TypedShipmentToolbar
                          activeTabId={activeTabId}
                          overAllShipmentCount={overAllShipmentCount}
                          selectAllAcrossFilters={selectAllAcrossFilters}
                          setSelectAllAcrossFilters={setSelectAllAcrossFilters}
                          excludedIds={excludedIds}
                          setExcludedIds={setExcludedIds}
                          appliedSearchQuery={(
                            filters.appliedSearchQuery ?? ''
                          ).trim()}
                          filtersPayload={toolbarFiltersPayload}
                          refreshCurrentView={() => {
                            if (activeViewType === 'calendar') {
                              const customTab = customTabs.find(
                                (tab) => tab.id === activeTabId
                              );

                              let payload =
                                filters.filtersPayloadRef?.current ?? null;

                              if (customTab) {
                                payload = buildShipmentFiltersPayload(
                                  [
                                    ...(customTab.savedFilterSet?.filters ??
                                      []),
                                    ...(filters.filterArray ?? []),
                                  ] as never,
                                  [
                                    ...(customTab.savedFilterSet
                                      ?.appliedFilters ?? []),
                                    ...(filters.appliedFilters ?? []),
                                  ]
                                );
                              }

                              const _ = fetchCalendarShipments(
                                viewBy,
                                payload,
                                calendarRangeRef.current ?? undefined,
                                true
                              );

                              return;
                            }

                            pageRef.current = 1;
                            setPage(1);
                            const _ = fetchShipments(
                              filters.appliedSearchQuery ?? '',
                              true,
                              1,
                              pageSize
                            );
                          }}
                          isCalendarActive={activeViewType === 'calendar'}
                          getViewportCalendarIds={() => {
                            return getVisibleCalendarShipmentIds(
                              calendarData,
                              calendarRangeRef.current
                            );
                          }}
                          selectableItems={selectableItems}
                          onApplyQuickFilter={applyQuickFilter}
                          isSearchTriggered={isSearchTriggered}
                          setSelectedShipmentStatuses={
                            setSelectedShipmentStatuses
                          }
                          appliedFilters={filters.appliedFilters}
                          selectedShipmentStatuses={selectedShipmentStatuses}
                          searchQuery={filters.searchQuery}
                          totalShipments={
                            activeViewType === 'calendar'
                              ? hasActiveFilters
                                ? (calendarTotalShipments ?? 0)
                                : calendarToolbarTotal
                              : firstPageCountPending &&
                                  shipmentCountData == null
                                ? undefined
                                : (totalShipments ?? 0)
                          }
                          selectionScopeCount={shipmentSelectionScopeCount}
                          CT_all_permissions={allPermissions}
                          page={page}
                          pageSize={pageSize}
                          fetchShipments={fetchShipments}
                          selectedIds={selectedIds}
                          setSelectedIds={setSelectedIds}
                          checked={checked}
                          setChecked={setChecked}
                          setShipmentsData={setShipmentsData}
                          setFilteredData={setFilteredData}
                          filteredData={filteredData}
                          isDrawerOpen={drawerState?.isOpen}
                          toggleDrawer={toggleDrawer}
                          legType={activeLegType}
                        />
                      )}

                      <ShipmentsFilterPanel
                        filterConfig={filtersConfig}
                        onDeleteSavedFilterSet={
                          handleDeleteSavedFilterSetFromDropdown
                        }
                        dropdownMaxHeight={dynamicDropdownHeight}
                        appliedSearchQuery={filters.appliedSearchQuery}
                        setAppliedSearchQuery={filters.setAppliedSearchQuery}
                        savedFilterKey='shipment'
                        auditSearch
                        token={token}
                        permissions={permissions}
                        appliedFilters={filters.appliedFilters}
                        setAppliedFilters={filters.setAppliedFilters}
                        filterArray={filters.filterArray}
                        setFilterArray={filters.setFilterArray}
                        setFiltersPayload={filters.setFiltersPayload}
                        searchQuery={filters.searchQuery}
                        setSearchQuery={filters.setSearchQuery}
                        isSearchVisible={filters.isSearchVisible}
                        setIsSearchVisible={filters.setIsSearchVisible}
                        page={page}
                        pageSize={pageSize}
                        onRunSearch={handleRunSearchFromPanel}
                        registerHandleApplyFilters={
                          filters.registerHandleApplyFilters
                        }
                        didRunInitialFetchRef={didRunInitialFetchRef}
                        useInternalSaveFilterSetModal={false}
                        onOpenSaveFilterSet={openCreateModal}
                        isShipmentToolbarFilter={true}
                        activeTabId={activeTabId}
                        customTabs={customTabs}
                      />
                    </Flex>
                    <Flex
                      align='center'
                      className='sm-d-none'
                      gap={8}
                      style={{ flexShrink: 0 }}
                    >
                      {!shouldHideSort && (
                        <ShipmentSort
                          sortMenu={sortMenu}
                          selectedSortLabel={selectedSortLabel}
                          sortOrder={sortOrder}
                          sortKey={sortKey}
                          onToggleOrder={handleSortOrderToggle}
                        />
                      )}

                      {!shouldCollapseAboveTable && (
                        <>
                          <ShipmentColumnsDropdown
                            token={token}
                            activeTabId={activeTabColumnsId}
                            unauthorize={unauthorize}
                            onChange={handleShipmentColumnsChange}
                            dropdownMaxHeight={dynamicDropdownHeight}
                            onColumnsSaved={async () => {
                              pageRef.current = 1;
                              setPage(1);

                              if (shipmentReportPayload) {
                                await resetShipmentReportToFirstPage();
                                return;
                              }

                              await fetchShipments(
                                filters.appliedSearchQuery ?? '',
                                true,
                                1,
                                pageSize
                              );
                            }}
                          />
                        </>
                      )}

                      <Flex gap={8}>
                        {hasOperationalLegView && (
                          <SwitchButtons
                            options={legTypeButtonsList}
                            defaultActive={activeLegType}
                            onChange={handleChangeLegType}
                          />
                        )}
                        <SwitchButtons
                          isIcons={true}
                          options={buttonsList}
                          defaultActive={activeViewType}
                          onChange={handleChangeViewType}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                  {/* </Row> */}
                </div>

                {activeViewType === 'calendar' ? (
                  <div className='h-fit mt-3'>
                    <CalendarView
                      tableView={true}
                      calendarApiRef={calendarApiRef}
                      viewMode='month'
                      filteredData={calendarData}
                      checked={checked}
                      selectedIds={selectedIds}
                      isSelectAllMode={isCalendarSelectAllMode}
                      excludedIds={excludedIds}
                      viewBy={viewBy}
                      setViewBy={setViewBy}
                      onRangeChange={handleCalendarRangeChange}
                      legType={activeLegType}
                      onToggleSelect={(item, nextSelected) => {
                        const id = String(item.shipment_id);
                        if (isCalendarSelectAllMode) {
                          setExcludedIds((prev) => {
                            if (nextSelected)
                              return prev.filter((curr) => curr !== id);
                            if (prev.includes(id)) return prev;
                            return [...prev, id];
                          });
                          return;
                        }
                        const fakeShipmentCard = calendarItemToShipment(item);
                        handleSelect(id, nextSelected, fakeShipmentCard);
                      }}
                      onShipmentClick={(shipmentId, alertName, shipment) => {
                        setDrawerSource('calendar');
                        setHighlightedShipmentId(null);
                        toggleDrawer(
                          'open',
                          'Detail',
                          shipmentId,
                          alertName,
                          shipment.leg_type
                        );
                      }}
                      onMoreClick={({
                        dateStr,
                        eventDatetime,
                        slotStart,
                        slotEnd,
                        source,
                      }) => {
                        moreDrawerSortRef.current = null;
                        moreDrawerPageRef.current = 1;

                        setMoreDrawerSource(source);
                        toggleDrawer('close', '', null, null);
                        setMoreDrawerViewBy(viewBy);
                        setMoreDrawerEventDatetime(eventDatetime ?? dateStr);
                        setMoreDrawerSlotStart(slotStart ?? null);
                        setMoreDrawerSlotEnd(slotEnd ?? null);
                        setMoreDrawerOpen(true);
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className={`shipment-table-fill${
                      shouldShowShipmentMobileCards
                        ? ' shipment-mobile-card-fill'
                        : ''
                    }`}
                  >
                    {shouldShowShipmentMobileCards ? (
                      <ShipmentMobileCards
                        shipments={virtualShipmentRows}
                        loading={tableLoading}
                        loadingMore={isFetchingNextPage}
                        hasMore={hasMoreShipments}
                        openShipmentId={effectiveOpenShipmentId}
                        highlightedShipmentId={highlightedShipmentId}
                        onOpenShipment={(id, shipment) => {
                          openShipmentDrawer(id, shipment);
                        }}
                        onLoadMore={handleLoadMoreShipments}
                        customerLegEnabled={worldCourierEnabled}
                        selectedIds={selectedIds}
                        checked={checked}
                        excludedIds={excludedIds}
                        isSelectAllMode={isSelectAllMode}
                        onSelectChange={(id, nextSelected, shipment) => {
                          if (isSelectAllMode) {
                            setExcludedIds((prev) => {
                              if (nextSelected)
                                return prev.filter((curr) => curr !== id);
                              if (prev.includes(id)) return prev;
                              return [...prev, id];
                            });
                            return;
                          }

                          handleSelect(
                            id,
                            nextSelected,
                            // eslint-disable-next-line no-restricted-syntax -- handleSelect only reads `status`, which ShipmentRow has
                            shipment as unknown as Shipment
                          );
                        }}
                      />
                    ) : (
                      <ShipmentVirtualTable
                        headerList={effectiveReportHeaders}
                        shipmentData={virtualShipmentRows}
                        activeLegType={activeLegType}
                        customerLegEnabled={worldCourierEnabled}
                        loading={tableLoading}
                        loadingMore={isFetchingNextPage}
                        hasMore={hasMoreShipments}
                        pageSize={pageSize}
                        checked={checked}
                        selectedIds={selectedIds}
                        excludedIds={excludedIds}
                        isSelectAllMode={isSelectAllMode}
                        openShipmentId={effectiveOpenShipmentId}
                        highlightedShipmentId={highlightedShipmentId}
                        onSelectChange={(id, nextSelected, shipment) => {
                          if (isSelectAllMode) {
                            setExcludedIds((prev) => {
                              if (nextSelected)
                                return prev.filter((curr) => curr !== id);
                              if (prev.includes(id)) return prev;
                              return [...prev, id];
                            });
                            return;
                          }

                          handleSelect(
                            id,
                            nextSelected,
                            // eslint-disable-next-line no-restricted-syntax -- handleSelect only reads `status`, which ShipmentRow has
                            shipment as unknown as Shipment
                          );
                        }}
                        onOpenShipment={(id, shipment) => {
                          setDrawerSource('list');
                          openShipmentDrawer(id, shipment);
                        }}
                        onOpenExecutionLeg={(
                          elId,
                          parentClId,
                          parentClReference
                        ) => {
                          setDrawerSource('list');
                          toggleDrawer(
                            'open',
                            'Detail',
                            elId,
                            parentClId
                              ? {
                                  navStack: [
                                    {
                                      id: parentClId,
                                      legType: 'CL' as const,
                                      reference: parentClReference,
                                    },
                                  ],
                                }
                              : undefined,
                            'EL'
                          );
                        }}
                        onShipmentAction={handleShipmentTableAction}
                        onLoadMore={handleLoadMoreShipments}
                        allPermissions={allPermissions}
                      />
                    )}
                  </div>
                )}
              </div>

              <ShipmentDrawer
                onLinkedShipmentClick={handleHighlightShipmentCard}
                // drawer prop types are stricter than the page's; runtime shape is unchanged
                allPermissions={allPermissions as Record<string, unknown>}
                drawerState={drawerState as ShipmentDrawerState}
                legType={drawerLegType}
                toggleDrawer={toggleDrawer}
                shipmentSuccess={shipmentSuccess}
                filterByLinkCode={filterByLinkCode}
                onEditShipmentSuccess={onEditShipmentSuccess}
                onQuoteFromShipment={
                  manualShipmentFlags.quote
                    ? handleQuoteFromShipment
                    : undefined
                }
                onBookFromShipment={
                  !manualShipmentFlags.book || !directBookingEnabled
                    ? undefined
                    : handleBookFromShipment
                }
                onGenerateReturnLabel={handleGenerateReturnLabel}
                onCocCommentCreated={handleCocCommentCreated}
                onShipmentCommentsRead={handleShipmentCommentsRead}
                onShipmentNotificationActivityChange={
                  handleShipmentNotificationActivityChange
                }
                detailRefreshKey={detailRefreshKey}
              />
            </Flex>
          </>
        )}

        <BulkUploadModal
          modalOpen={showBulkUploadModal}
          setModalOpen={setShowBulkUploadModal}
        />
      </div>

      {openCreateShipmentDrawer && (
        <CommonDrawer
          open={openCreateShipmentDrawer}
          onClose={() => handleCloseDrawer()}
          width={520}
        >
          <CreateShipmentDrawer
            handleCloseDrawer={handleCloseDrawer}
            onSuccess={handleCreateShipmentSuccess}
          />
        </CommonDrawer>
      )}

      {openBookShipmentDrawer && (
        <CommonDrawer
          open={openBookShipmentDrawer}
          onClose={() => {
            setOpenBookShipmentDrawer(false);
            setBookPrefillShipment(undefined);
          }}
          width={520}
        >
          <BookShipmentDrawer
            key={bookPrefillShipment?.id ?? 'menu-book'}
            onClose={() => {
              setOpenBookShipmentDrawer(false);
              setBookPrefillShipment(undefined);
            }}
            shellShipment={bookPrefillShipment}
            onPromoteSuccess={() => {
              shipmentSuccess();
              setDetailRefreshKey((k) => k + 1);
            }}
          />
        </CommonDrawer>
      )}

      {openQuoteShipmentDrawer && (
        <CommonDrawer
          open={openQuoteShipmentDrawer}
          onClose={() => {
            setOpenQuoteShipmentDrawer(false);
            setQuotePrefillShipment(undefined);
            setQuoteDrawerMode('quote');
          }}
          width={520}
        >
          <QuoteShipmentDrawer
            onClose={() => {
              setOpenQuoteShipmentDrawer(false);
              setQuotePrefillShipment(undefined);
              setQuoteDrawerMode('quote');
            }}
            mode={quoteDrawerMode}
            shellShipment={
              quoteDrawerMode === 'quote' ? quotePrefillShipment : undefined
            }
            returnSourceShipment={
              quoteDrawerMode === 'return' ? quotePrefillShipment : undefined
            }
            onPromoteSuccess={() => setDetailRefreshKey((k) => k + 1)}
          />
        </CommonDrawer>
      )}

      {openCreateShellDrawer && (
        <CommonDrawer
          open={openCreateShellDrawer}
          onClose={() => setOpenCreateShellDrawer(false)}
          width={520}
        >
          <CreateShellShipmentDrawer
            onClose={() => setOpenCreateShellDrawer(false)}
            onSuccess={(shipment) => {
              if (shipment?.id) {
                toggleDrawer(
                  'open',
                  'Detail',
                  shipment.id,
                  null,
                  shipment.leg_type
                );
              }
            }}
          />
        </CommonDrawer>
      )}

      {moreDrawerOpen && (
        <CommonDrawer
          open={moreDrawerOpen}
          onClose={() => {
            setMoreDrawerOpen(false);
            moreDrawerPageRef.current = 1;
          }}
          width={552}
          className='remove-header-line-calendar-view'
        >
          <div className='h-full'>
            {moreDrawerEventDatetime ? (
              <CalendarViewMore
                appliedFilterArray={filters.filterArray}
                filtersPayload={filters.filtersPayloadRef.current}
                activeTabId={activeTabId}
                availableHourSlots={availableHourSlots}
                checked={checked}
                initialPage={moreDrawerPageRef.current}
                onPageChange={(p) => {
                  moreDrawerPageRef.current = p;
                }}
                initialSortKey={moreDrawerSortRef.current?.key}
                initialSortOrder={moreDrawerSortRef.current?.order}
                onSortChange={(sort) => {
                  moreDrawerSortRef.current = sort;
                }}
                source={moreDrawerSource}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                isSelectAllMode={isCalendarSelectAllMode}
                excludedIds={excludedIds}
                setExcludedIds={setExcludedIds}
                // calendar drawer narrows status values to string
                setSelectedShipmentStatuses={
                  setSelectedShipmentStatuses as Dispatch<
                    SetStateAction<Record<string, string | undefined>>
                  >
                }
                slotStart={moreDrawerSlotStart}
                slotEnd={moreDrawerSlotEnd}
                token={token}
                viewBy={moreDrawerViewBy}
                eventDatetime={moreDrawerEventDatetime}
                onUnauthorized={handleUnauthorized}
                tabValue='timeliness'
                chartType='transit_time'
                onShipmentClick={(shipmentId) => {
                  returnToMoreDrawerRef.current = true;
                  setMoreDrawerOpen(false);
                  setDrawerSource('calendar');
                  setHighlightedShipmentId(null);
                  toggleDrawer(
                    'open',
                    'Detail',
                    shipmentId,
                    {
                      isFromHome: true,
                    },
                    activeLegType
                  );
                }}
                initialSearch={calendarSearchQuery}
                initialTeams={calendarTeamIds}
                initialServiceLevels={calendarCourierIds}
                legType={activeLegType}
              />
            ) : null}
          </div>
        </CommonDrawer>
      )}
      <SavedFilterSetModal
        permissions={permissions}
        open={savedFilterModalOpen}
        onClose={closeModal}
        onDelete={handleDelete}
        editingFilterSet={editingFilterSet}
        currentAppliedFilters={
          filtersForSavedFilterSetModal.currentAppliedFilters
        }
        currentFilterArray={filtersForSavedFilterSetModal.currentFilterArray}
        onSave={handleSave}
      />
      <ViewFiltersModal
        open={viewFiltersModalOpen}
        tab={viewFiltersTab}
        onClose={handleCloseViewFiltersModal}
      />

      {openNotificationModal && tableActionShipmentID && (
        <PerShipmentNotificationModal
          // eslint-disable-next-line no-restricted-syntax -- modal's `selected` prop is non-nullable but null means "new"
          selected={null as unknown as NotificationData}
          fetchNotifications={() => {}}
          shipmentId={tableActionShipmentID}
          notificationModalType={notificationType}
          openNotificationModal={openNotificationModal}
          setOpenNotificationModal={setOpenNotificationModal}
          onNotificationCreated={
            notificationType === 1
              ? () => {
                  handleShipmentNotificationActivityChange(
                    tableActionShipmentID,
                    'on'
                  );
                }
              : undefined
          }
        />
      )}

      {shareShipmentModalOpen && tableActionShipmentID ? (
        <CustomModalWithoutButton
          modalOpen={shareShipmentModalOpen}
          setModalOpen={setShareShipmentModalOpen}
          modalTitle={t('shareShipment')}
          modalText=''
          modalSize={524}
        >
          <ShareShipment shipmentId={tableActionShipmentID} token={token} />
        </CustomModalWithoutButton>
      ) : null}
      {archiveShipmentModalOpen && tableActionShipmentID && (
        <ArchiveShipmentModal
          checkTokenExpiry={() => checkTokenExpiry() ?? ''}
          shipmentId={tableActionShipmentID}
          closeDeleteModal={closeDeleteModal}
          deleteShipmentModalOpen={archiveShipmentModalOpen}
          setDeleteShipmentModalOpen={setArchiveShipmentModalOpen}
          unauthorize={handleUnauthorized}
        />
      )}
      {showDeliveredModal && selectedShipmentForDelivered && (
        <MarkAsDeliveredModal
          shipmentData={
            // eslint-disable-next-line no-restricted-syntax -- ShipmentRow lacks most SingleShipmentDataExtended fields; the modal reads only id/shipment_status
            selectedShipmentForDelivered as unknown as SingleShipmentDataExtended & {
              shipment_status?: string;
            }
          }
          type={'single'}
          open={showDeliveredModal}
          onCancel={() => setShowDeliveredModal(false)}
          onSuccess={handleMarkAsDelivered}
        />
      )}

      <CustomDeleteModal
        visible={dismissAlertsModalVisible}
        onClose={() => {
          setDismissAlertsModalVisible(false);
          setShipmentIdToDismiss(null);
        }}
        onConfirm={handleConfirmDismiss}
        title={'Dismiss all alerts'}
        message={`You’re about to dismiss all alerts for this shipment`}
        cancelText='Cancel'
        confirmText={'Dismiss all alerts'}
        confirmColor='var(--danger)'
      />
    </div>
  );
};

export default Shipments;
